/**
 * Firebase Integration Layer (modular v10+) for What To Eat?
 *
 * PRESERVES runtime contract:
 * - window.__wtE_clientMessages
 * - window.__wtE_menuItems
 * - window.__wtE_reviews
 * - window.__wtE_allMenuItems
 * - window.firebaseSignUp / window.firebaseSignIn / window.firebaseSignOut
 * - window.firebaseSaveOrder / window.firebaseSaveMenuItem / window.firebaseUpdateOrderStatus / window.firebaseSaveReview
 * - window.syncAllMenuItems
 *
 * This file is an ES module and MUST be loaded with:
 *   <script type="module" src="firebase-integration.js"></script>
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signOut as authSignOut,
  onAuthStateChanged
} from "https://www.gstatic.com/firebasejs/12.18.0/firebase-auth.js";

import {
  arrayUnion,
  serverTimestamp,
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  deleteDoc,
  getCountFromServer
} from "https://www.gstatic.com/firebasejs/12.18.0/firebase-firestore.js";

(function () {
  let firebaseReady = false;
  let firebaseDb = null;
  let firebaseAuth = null;

  let currentUser = null;
  let userRole = null; // 'client' or 'provider'

  let unsubscribeListeners = [];

  // Max docs per paginated listener — keeps initial load fast and cheap
  const ORDERS_PAGE_SIZE = 50;
  const ADMIN_PAGE_SIZE = 100;

  const fallbackMarketComplianceService = {
    buildMarketCompliancePolicy(orderPayload = {}) {
      const source = orderPayload && typeof orderPayload === 'object' ? orderPayload : {};
      const complianceRoot = source.compliance && typeof source.compliance === 'object' ? source.compliance : {};
      const merged = { ...complianceRoot, ...source };
      const marketMap = {
        US: { currency: 'USD', taxRate: 0.0825, refundWindowHours: 24, cancellationWindowHours: 2, payoutDelayDays: 7, payoutFeePercent: 0.05 },
        CA: { currency: 'CAD', taxRate: 0.13, refundWindowHours: 24, cancellationWindowHours: 2, payoutDelayDays: 7, payoutFeePercent: 0.05 },
        GB: { currency: 'GBP', taxRate: 0.2, refundWindowHours: 48, cancellationWindowHours: 4, payoutDelayDays: 10, payoutFeePercent: 0.04 },
        EU: { currency: 'EUR', taxRate: 0.2, refundWindowHours: 48, cancellationWindowHours: 4, payoutDelayDays: 10, payoutFeePercent: 0.04 },
        default: { currency: 'USD', taxRate: 0, refundWindowHours: 24, cancellationWindowHours: 2, payoutDelayDays: 7, payoutFeePercent: 0.05 }
      };
      const country = String(merged.country || merged.clientCountry || merged.providerCountry || complianceRoot.country || 'US').trim();
      const code = String(country).toUpperCase();
      const marketCode = code === 'US' || code === 'USA' ? 'US' : code === 'CA' || code === 'CAN' ? 'CA' : code === 'GB' || code === 'UK' ? 'GB' : ['DE','FR','IT','ES','NL','BE','AT','IE','PT','LU','PL','SE','FI','DK','CZ','SK','SI','RO','HR','GR','EE','LV','LT','CY','MT'].includes(code) ? 'EU' : 'default';
      const market = marketMap[marketCode] || marketMap.default;
      const price = Number(merged.price ?? complianceRoot.price ?? 0) || 0;
      const taxRate = Number(merged.taxRate ?? complianceRoot.taxRate ?? market.taxRate ?? 0);
      const payoutFeePercent = Number(merged.payoutFeePercent ?? complianceRoot.payoutFeePercent ?? market.payoutFeePercent ?? 0.05);
      const taxAmount = Number((merged.taxAmount ?? complianceRoot.taxAmount ?? (price * taxRate)).toFixed(2));
      const payoutFeeAmount = Number((merged.payoutFeeAmount ?? complianceRoot.payoutFeeAmount ?? (price * payoutFeePercent)).toFixed(2));
      const refundWindowHours = Number(merged.refundWindowHours ?? complianceRoot.refundWindowHours ?? market.refundWindowHours ?? 24);
      const cancellationWindowHours = Number(merged.cancellationWindowHours ?? complianceRoot.cancellationWindowHours ?? market.cancellationWindowHours ?? 2);
      const payoutDelayDays = Number(merged.payoutDelayDays ?? complianceRoot.payoutDelayDays ?? market.payoutDelayDays ?? 7);
      return {
        marketCode,
        country: country || 'US',
        currency: merged.currency || complianceRoot.currency || market.currency || 'USD',
        taxRate: Number(Math.max(0, taxRate).toFixed(4)),
        taxAmount,
        refundWindowHours,
        cancellationWindowHours,
        payoutDelayDays,
        payoutFeePercent: Number(Math.max(0, payoutFeePercent).toFixed(4)),
        payoutFeeAmount,
        refundPolicy: merged.refundPolicy || complianceRoot.refundPolicy || `Refunds are available within ${refundWindowHours} hours for eligible orders before completion.`,
        cancellationPolicy: merged.cancellationPolicy || complianceRoot.cancellationPolicy || `Orders may be cancelled within ${cancellationWindowHours} hours of placement unless the provider has already started preparation.`,
        payoutPolicy: merged.payoutPolicy || complianceRoot.payoutPolicy || `Provider payouts are processed after ${payoutDelayDays} days and a ${Number((payoutFeePercent * 100)).toFixed(2)}% platform charge may apply.`,
        settlementNotes: merged.settlementNotes || complianceRoot.settlementNotes || market.settlementNotes || 'Target-market compliance defaults are applied until a market-specific configuration is confirmed.'
      };
    }
  };

  const marketComplianceService = window.marketComplianceService || fallbackMarketComplianceService;

  async function ensureFirebaseServicesReady() {
    if (firebaseReady && firebaseAuth && firebaseDb) return true;

    if (window.firebaseAuthRaw && (window.firebaseDbRaw || window.firebaseDbInstance)) {
      // Use the RAW modular Auth instance for modular SDK functions
      // (signInWithEmailAndPassword, onAuthStateChanged, authSignOut, etc.)
      firebaseAuth = window.firebaseAuthRaw;
      // Use raw modular instance for modular SDK functions (collection, doc, etc.)
      firebaseDb = window.firebaseDbRaw || window.firebaseDbInstance;
      firebaseReady = true;
      return true;
    }

    if (window.firebaseAuthInstance && (window.firebaseDbRaw || window.firebaseDbInstance)) {
      // Fallback: if firebaseAuthRaw is not available, unwrap the v8-compatible
      // auth wrapper (its _raw property points to the modular instance).
      firebaseAuth = window.firebaseAuthInstance._raw || window.firebaseAuthInstance;
      // Use raw modular instance for modular SDK functions (collection, doc, etc.)
      firebaseDb = window.firebaseDbRaw || window.firebaseDbInstance;
      firebaseReady = true;
      return true;
    }

    if (typeof window.ensureFirebaseReady === "function") {
      const res = await window.ensureFirebaseReady();
      if (res && res.auth && res.db) {
        firebaseAuth = res.auth;
        firebaseDb = res.db;
        firebaseReady = true;
        return true;
      }
    }

    return false;
  }

  function stopAllListeners() {
    console.log("[Firestore Sync] Stopping", unsubscribeListeners.length, "listeners");
    unsubscribeListeners.forEach((unsub) => {
      try {
        if (typeof unsub === "function") unsub();
      } catch {}
    });
    unsubscribeListeners = [];
  }

  async function refreshProviderApprovalClaim() {
    if (!firebaseAuth || !firebaseAuth.currentUser || typeof firebaseAuth.currentUser.getIdToken !== "function") {
      return false;
    }

    const user = firebaseAuth.currentUser;
    try {
      const result = await user.getIdTokenResult(true);
      const claims = (result && result.claims) || {};
      if (claims.role === "provider" && claims.approved === true) {
        return true;
      }
    } catch (e) {
      console.warn("[Firestore Sync] Failed to read provider approval claim:", e && e.message ? e.message : e);
    }

    try {
      const projectId = (window.firebaseConfig && window.firebaseConfig.projectId) || "what-to-eat-e5ce9";
      const idToken = await user.getIdToken();
      const response = await fetch(`https://us-central1-${projectId}.cloudfunctions.net/getProviderLoginStatus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ refresh: true })
      });

      const payload = response && typeof response.json === "function" ? await response.json().catch(() => ({})) : {};
      if (!response || !response.ok) {
        console.warn("[Firestore Sync] Provider approval refresh failed:", payload && payload.error ? payload.error : response && response.statusText ? response.statusText : "unknown");
        return false;
      }

      const refreshed = await user.getIdTokenResult(true);
      const claims = (refreshed && refreshed.claims) || {};
      return claims.role === "provider" && claims.approved === true;
    } catch (e) {
      console.warn("[Firestore Sync] Provider approval refresh request failed:", e && e.message ? e.message : e);
      return false;
    }
  }

  // ==================== ADMIN REAL-TIME LISTENERS ====================
  // Sets up onSnapshot listeners for clients and providers collections
  // so the admin dashboard auto-updates when data changes (new signups, deletions, etc.)
  let adminUnsubscribeClients = null;
  let adminUnsubscribeProviders = null;

  async function setupAdminRealTimeListeners() {
    if (typeof isAdminLoggedIn === 'function' && !isAdminLoggedIn()) {
      console.log("[Firestore Sync] Skipping admin listeners: no active admin session");
      return;
    }
    if (!firebaseReady || !firebaseDb) return;

    // If already listening, don't duplicate
    if (adminUnsubscribeClients || adminUnsubscribeProviders) {
      console.log("[Firestore Sync] Admin listeners already active, skipping");
      return;
    }

    try {
      if (typeof window.bootstrapAdminData === 'function') {
        await window.bootstrapAdminData();
      }
      if (typeof renderAdminStats === 'function') renderAdminStats();
      if (typeof renderAdminClients === 'function') renderAdminClients();
      if (typeof renderAdminProviders === 'function') renderAdminProviders();
    } catch (e) {
      console.warn("[Firestore Sync] Initial admin bootstrap failed before listener setup:", e && e.message ? e.message : e);
    }

    console.log("[Firestore Sync] Setting up admin real-time listeners for clients & providers...");

    // Listen for changes to the clients collection (most recent 100)
    adminUnsubscribeClients = onSnapshot(
      query(collection(firebaseDb, "clients"), orderBy("createdAt", "desc"), limit(ADMIN_PAGE_SIZE)),
      (snapshot) => {
        console.log("[Firestore Sync] Clients collection changed, refreshing admin data...");
        snapshot.docChanges().forEach(change => {
          if (change.type === "added") {
            console.log("[Firestore Sync] New client added:", change.doc.id);
          } else if (change.type === "removed") {
            console.log("[Firestore Sync] Client removed:", change.doc.id);
          }
        });

        // Update local arrays and re-render admin panels
        refreshAdminDataAfterChange();
      },
      (error) => {
        console.warn("[Firestore Sync] Admin clients listener error:", error);
      }
    );

    // Listen for changes to the providers collection (most recent 100)
    adminUnsubscribeProviders = onSnapshot(
      query(collection(firebaseDb, "providers"), orderBy("createdAt", "desc"), limit(ADMIN_PAGE_SIZE)),
      (snapshot) => {
        console.log("[Firestore Sync] Providers collection changed, refreshing admin data...");
        snapshot.docChanges().forEach(change => {
          if (change.type === "added") {
            console.log("[Firestore Sync] New provider added:", change.doc.id);
          } else if (change.type === "removed") {
            console.log("[Firestore Sync] Provider removed:", change.doc.id);
          } else if (change.type === "modified") {
            console.log("[Firestore Sync] Provider modified:", change.doc.id);
          }
        });

        // Update local arrays and re-render admin panels
        refreshAdminDataAfterChange();
      },
      (error) => {
        console.warn("[Firestore Sync] Admin providers listener error:", error);
      }
    );

    // Also listen for menuItems changes to refresh stats (most recent 100)
    const menuUnsub = onSnapshot(
      query(collection(firebaseDb, "menuItems"), orderBy("createdAt", "desc"), limit(ADMIN_PAGE_SIZE)),
      () => {
        // Re-render stats panel if admin is logged in
        if (typeof isAdminLoggedIn === 'function' && isAdminLoggedIn()) {
          if (typeof renderAdminStats === 'function') {
            setTimeout(() => renderAdminStats(), 100);
          }
        }
      },
      () => {} // ignore errors for menuItems listener
    );
    unsubscribeListeners.push(menuUnsub);

    // Also listen for orders changes to refresh admin orders panel (most recent 100)
    const ordersUnsub = onSnapshot(
      query(collection(firebaseDb, "orders"), orderBy("createdAt", "desc"), limit(ADMIN_PAGE_SIZE)),
      () => {
        // Re-render admin panels if admin is logged in
        if (typeof isAdminLoggedIn === 'function' && isAdminLoggedIn()) {
          if (typeof refreshAdminDataAfterChange === 'function') {
            refreshAdminDataAfterChange();
          }
        }
      },
      () => {} // ignore errors for orders listener
    );
    unsubscribeListeners.push(ordersUnsub);

    // Push admin listeners to cleanup list
    // Note: We don't push to unsubscribeListeners because we manage them separately
    // They need to persist across auth state changes for the admin session

    console.log("[Firestore Sync] Admin real-time listeners established");
  }

  function stopAdminRealTimeListeners() {
    if (typeof adminUnsubscribeClients === 'function') {
      try { adminUnsubscribeClients(); } catch {}
      adminUnsubscribeClients = null;
    }
    if (typeof adminUnsubscribeProviders === 'function') {
      try { adminUnsubscribeProviders(); } catch {}
      adminUnsubscribeProviders = null;
    }
    console.log("[Firestore Sync] Admin real-time listeners stopped");
  }

  // Helper to refresh admin data after a Firestore change. Listener bursts
  // (e.g. initial clients + providers + orders snapshots) are debounced into
  // a single refresh.
  let adminRefreshTimer = null;
  let adminRefreshWaiters = [];
  function refreshAdminDataAfterChange() {
    return new Promise((resolve) => {
      adminRefreshWaiters.push(resolve);
      if (adminRefreshTimer) clearTimeout(adminRefreshTimer);
      adminRefreshTimer = setTimeout(() => {
        adminRefreshTimer = null;
        const waiters = adminRefreshWaiters;
        adminRefreshWaiters = [];
        runAdminDataRefresh().finally(() => waiters.forEach((done) => done()));
      }, 300);
    });
  }

  async function runAdminDataRefresh() {
    const adminActive = typeof isAdminLoggedIn === 'function' && isAdminLoggedIn();

    if (adminActive && typeof window.refreshAllAdminPanels === 'function') {
      try {
        // refreshAllAdminPanels performs one forced fetch shared by all panels.
        await window.refreshAllAdminPanels();
      } catch (e) {
        console.warn("[Firestore Sync] refreshAllAdminPanels failed:", e.message);
      }
      return;
    }

    if (typeof window.bootstrapAdminData === 'function') {
      try {
        await window.bootstrapAdminData({ force: true });
      } catch (e) {
        console.warn("[Firestore Sync] bootstrapAdminData failed:", e.message);
      }
    }

    if (adminActive) {
      // Fallback: re-render individual panels
      try {
        if (typeof renderAdminStats === 'function') renderAdminStats();
        if (typeof renderAdminSubscriptions === 'function') renderAdminSubscriptions();
        if (typeof renderAdminClients === 'function') renderAdminClients();
        if (typeof renderAdminProviders === 'function') renderAdminProviders();
      } catch (e) {
        console.warn("[Firestore Sync] Individual admin panel re-render failed:", e.message);
      }
    }
  }

  // Expose functions globally so admin-delete-handlers.js can use them
  window.setupAdminRealTimeListeners = setupAdminRealTimeListeners;
  window.stopAdminRealTimeListeners = stopAdminRealTimeListeners;
  window.refreshAdminDataAfterChange = refreshAdminDataAfterChange;

  function syncSharedData() {
    if (!firebaseReady || !firebaseDb) return;

    const reviewsQ = query(
      collection(firebaseDb, "reviews"),
      orderBy("createdAt", "desc"),
      limit(ADMIN_PAGE_SIZE)
    );
    const unsub = onSnapshot(
      reviewsQ,
      (snapshot) => {
        const reviewsData = [];
        snapshot.forEach((d) => reviewsData.push(d.data()));
        window.__wtE_reviews = reviewsData;
        console.log("[Firestore Sync] Synced", reviewsData.length, "reviews");
      },
      (error) => console.log("[Firestore Sync] Reviews listener error:", error)
    );

    unsubscribeListeners.push(unsub);
  }

  function startUserSync(role) {
    if (!firebaseReady || !currentUser || !firebaseDb) return;

    const authUid = currentUser.uid;
    console.log("[Firestore Sync] Starting real-time sync for", role, authUid);

    // Before starting sync, ensure the user has the proper role claim set
    // by calling the setUserRole Cloud Function if needed.
    ensureUserRoleClaim(role).catch(e => {
      console.warn("[Firestore Sync] Role claim sync skipped:", e.message);
    });

    if (role === "provider") {
      refreshProviderApprovalClaim().then((isApproved) => {
        if (!isApproved) {
          console.warn("[Firestore Sync] Provider token is missing approved claim; Firestore sync is being blocked until approval refresh succeeds.");
        }
      }).catch(() => {});
    }

    stopAllListeners();

    if (role === "client") {
      // Your Firestore orders use clientId == client uid (auth uid) (app.js sets clientId: currentUser.uid)
      const ordersQ = query(
        collection(firebaseDb, "orders"),
        where("clientId", "==", authUid),
        orderBy("createdAt", "desc"),
        limit(ORDERS_PAGE_SIZE)
      );

      const unsubOrders = onSnapshot(
        ordersQ,
        (snapshot) => {
          const ordersData = [];
          snapshot.forEach((d) => ordersData.push({ id: d.id, ...(d.data() || {}) }));
          // Backward-compatible runtime contract for app.js:
          // app.js bridges from window.__wtE_orders (not __wtE_clientMessages)
          window.__wtE_clientMessages = ordersData;
          window.__wtE_orders = ordersData;
          console.log("[Firestore Sync] Synced", ordersData.length, "orders for client");
          // Bridge new orders into app.js local array so the DOM updates instantly
          if (typeof window.bridgeFirestoreOrdersToLocal === 'function') {
            window.bridgeFirestoreOrdersToLocal(ordersData);
          }
          if (typeof renderClientOrders === "function") renderClientOrders();
        },
        (error) => console.log("[Firestore Sync] Orders listener error:", error)
      );

      unsubscribeListeners.push(unsubOrders);
    } else if (role === "provider") {
      // Provider schema in Firestore (single contract):
      // - orders.providerId == provider auth UID (string)
      // - menuItems.providerId == provider auth UID (string)
      //
      // App.js writes providerId as a Firebase Auth UID string.

      const providerDoc = (typeof currentProviderDoc === "object" && currentProviderDoc) ? currentProviderDoc : null;

      // Accept multiple possible shapes and normalize:
      // providerDoc.providerId can come as int64/number/string depending on SDK/serialization.
      let providerIdKey = null;
      if (providerDoc) {
        if (providerDoc.uid != null) providerIdKey = providerDoc.uid;
        else if (providerDoc.providerId != null) providerIdKey = providerDoc.providerId;
        else if (providerDoc.id != null) providerIdKey = providerDoc.id;
      }

      // NOTE: providerId is stored as provider UID (string) in your current schema.
      // This integration must query using STRING(providerIdKey) to match app.js writes.

      // currentProviderDoc is discovered via email lookup in getUserRole(); normalize queries to string.



      // If providerIdKey is missing, attempt a single email-based Firestore doc lookup (still server/Firestore-only).
      // This addresses cases where getUserRole() matched a provider doc that doesn't contain providerId.
      if (providerIdKey == null) {
        console.warn("[Firestore Sync] providerIdKey missing; attempting provider email lookup for providerIdKey.", {
          providerDoc
        });

        const authUser = firebaseAuth.currentUser;
        const authEmail = authUser && authUser.email ? String(authUser.email).toLowerCase() : null;

        // Always sync shared data (reviews, all-menu-items for search) even if provider orders/menu can’t be scoped.
        syncSharedData();

        if (!authEmail) return;

        // One-time lookup to find the provider doc that contains providerId.
        // NOTE: startUserSync is not async; we do a best-effort promise chain.
        const providerEmailQ = query(
          collection(firebaseDb, "providers"),
          where("email", "==", authEmail)
        );

        getDocs(providerEmailQ)
          .then((snap) => {
            if (snap.empty) return null;
            let found = null;
            snap.forEach((d) => {
              if (!found) found = d.data();
            });
            if (!found) return null;

            let key = found.uid != null ? found.uid : (found.providerId != null ? found.providerId : (found.id != null ? found.id : null));
            // Keep providerId as string (provider auth UID)
            if (key == null) return null;
            const asString = String(key);
            if (asString.trim() === "") return null;
            return asString;
          })
          .then((resolvedProviderIdKey) => {
            if (resolvedProviderIdKey == null) {
              console.warn("[Firestore Sync] providerIdKey still missing after email lookup; provider scoped sync skipped.");
              return;
            }

            const menuQ2 = query(
              collection(firebaseDb, "menuItems"),
              where("providerId", "==", String(resolvedProviderIdKey)),
              orderBy("createdAt", "desc"),
              limit(ADMIN_PAGE_SIZE)
            );

            const unsubMenu2 = onSnapshot(
              menuQ2,
              (snapshot) => {
                const menuData = [];
                snapshot.forEach((d) => menuData.push(d.data()));
                window.__wtE_menuItems = menuData;
                if (typeof window.bridgeFirestoreMenuItemsToLocal === "function") {
                  window.bridgeFirestoreMenuItemsToLocal(menuData);
                }
                console.log("[Firestore Sync] Synced", menuData.length, "menu items for provider (post-lookup)");
                if (typeof renderMenuList === "function") renderMenuList();
              },
              (error) => console.log("[Firestore Sync] Menu listener error:", error)
            );

            const providerOrdersQ2 = query(
              collection(firebaseDb, "orders"),
              where("providerId", "==", String(resolvedProviderIdKey)),
              orderBy("createdAt", "desc"),
              limit(ORDERS_PAGE_SIZE)
            );

            const unsubOrders2 = onSnapshot(
              providerOrdersQ2,
              (snapshot) => {
                const ordersData = [];
                snapshot.forEach((d) => ordersData.push({ id: d.id, ...(d.data() || {}) }));
  // Backward-compatible runtime contract for app.js
                // NOTE: app.js expects window.__wtE_orders / __wtE_clientMessages with stable order ids.
                window.__wtE_clientMessages = ordersData;
                window.__wtE_orders = ordersData;
                // Bridge new orders into app.js local array so the DOM updates instantly
                if (typeof window.bridgeFirestoreOrdersToLocal === 'function') {
                  window.bridgeFirestoreOrdersToLocal(ordersData);
                }
                console.log("[Firestore Sync] Synced", ordersData.length, "orders for provider (post-lookup)");
                if (typeof renderProviderOrders === "function") renderProviderOrders();
              },
              (error) => console.log("[Firestore Sync] Provider orders listener error:", error)
            );

            unsubscribeListeners.push(unsubMenu2, unsubOrders2);
          })
          .catch(() => {});
        return;
      }

      const menuQ = query(
        collection(firebaseDb, "menuItems"),
        where("providerId", "==", String(providerIdKey)),
        orderBy("createdAt", "desc"),
        limit(ADMIN_PAGE_SIZE)
      );

      const unsubMenu = onSnapshot(
        menuQ,
        (snapshot) => {
          const menuData = [];
          snapshot.forEach((d) => menuData.push(d.data()));
          window.__wtE_menuItems = menuData;
          if (typeof window.bridgeFirestoreMenuItemsToLocal === "function") {
            window.bridgeFirestoreMenuItemsToLocal(menuData);
          }
          console.log("[Firestore Sync] Synced", menuData.length, "menu items for provider");
          if (typeof renderMenuList === "function") renderMenuList();
        },
        (error) => console.log("[Firestore Sync] Menu listener error:", error)
      );

      const providerOrdersQ = query(
        collection(firebaseDb, "orders"),
        where("providerId", "==", String(providerIdKey)),
        orderBy("createdAt", "desc"),
        limit(ORDERS_PAGE_SIZE)
      );

      const unsubOrders = onSnapshot(
        providerOrdersQ,
        (snapshot) => {
          const ordersData = [];
          snapshot.forEach((d) => ordersData.push({ id: d.id, ...(d.data() || {}) }));
          // Backward-compatible runtime contract for app.js:
          // app.js bridges from window.__wtE_orders (not __wtE_clientMessages)
          window.__wtE_clientMessages = ordersData;
          window.__wtE_orders = ordersData;
          // Bridge new orders into app.js local array so the DOM updates instantly
          if (typeof window.bridgeFirestoreOrdersToLocal === 'function') {
            window.bridgeFirestoreOrdersToLocal(ordersData);
          }
          console.log("[Firestore Sync] Synced", ordersData.length, "orders for provider");
          if (typeof renderProviderOrders === "function") renderProviderOrders();
        },
        (error) => console.log("[Firestore Sync] Provider orders listener error:", error)
      );

      unsubscribeListeners.push(unsubMenu, unsubOrders);
    }

    syncSharedData();
  }

  let currentProviderDoc = null;

  // Live subscription sync for the currently logged-in provider document (providers/{uid})
  // Used to avoid "must refresh" UI after admin suspend/activate.
  let unsubscribeProviderSubscription = null;

  function normalizeProviderDocForUI(d) {
    const docData = d || {};
    return {
      id: docData.id ?? docData.uid ?? null,
      uid: docData.uid ?? docData.id ?? null,
      name: docData.name,
      email: docData.email,
      type: docData.type,
      address: docData.address,
      country: docData.country,
      acceptsCustomRequests: docData.acceptsCustomRequests,

      subscriptionStatus: docData.subscriptionStatus,
      subscriptionPlan: docData.subscriptionPlan,
      subscriptionStart: docData.subscriptionStart,
      subscriptionEnd: docData.subscriptionEnd,
      subscriptionApprovedBy: docData.subscriptionApprovedBy,
      subscriptionApprovedAt: docData.subscriptionApprovedAt,
      subscriptionRejectionReason: docData.subscriptionRejectionReason,
      subscriptionSuspendedReason: docData.subscriptionSuspendedReason,
      subscriptionNotes: docData.subscriptionNotes,
      createdAt: docData.createdAt
    };
  }

  function startLiveProviderSubscriptionSync() {
    // ensure currentUser/provider exists
    if (!firebaseReady || !firebaseDb || !currentUser) return;
    if (!currentUser.uid) return;

    stopLiveProviderSubscriptionSync();

    const providerUid = String(currentUser.uid);

    // providers/{uid} doc id is expected to be auth UID in your schema.
    const providerRef = doc(firebaseDb, "providers", providerUid);

    console.log("[Firestore Sync] Starting live provider subscription listener for:", providerUid);

    unsubscribeProviderSubscription = onSnapshot(
      providerRef,
      (snap) => {
        if (!snap || !snap.exists()) return;

        const data = snap.data() || {};
        const normalized = normalizeProviderDocForUI(data);

        // Expose normalized doc to app.js so it can re-render immediately.
        window.__wtE_providerSubscriptionSnapshot?.(normalized);

        // Keep integration-level reference up-to-date as well
        currentProviderDoc = data;
      },
      (error) => {
        console.log("[Firestore Sync] Provider subscription listener error:", error);
      }
    );
  }

  function stopLiveProviderSubscriptionSync() {
    if (typeof unsubscribeProviderSubscription === "function") {
      try { unsubscribeProviderSubscription(); } catch {}
    }
    unsubscribeProviderSubscription = null;
  }

  async function resolveServerRoleForCurrentUser(user) {
    if (!user || !user.uid) return null;

    try {
      const idTokenResult = await user.getIdTokenResult(true);
      const claims = (idTokenResult && idTokenResult.claims) || {};
      const tokenRole = String(claims.role || "").toLowerCase();
      const approved = claims.approved === true || claims.providerApproved === true;

      if (claims.admin === true || tokenRole === "admin") return "admin";
      if (tokenRole === "client") return "client";
      if (tokenRole === "provider") return "provider";
    } catch (e) {
      console.warn("[Firestore Sync] Unable to resolve canonical role claim:", e.message || e);
    }

    try {
      const providerSnap = await getDoc(doc(firebaseDb, "providers", String(user.uid))).catch(() => null);
      if (providerSnap && providerSnap.exists()) return "provider";
      const clientSnap = await getDoc(doc(firebaseDb, "clients", String(user.uid))).catch(() => null);
      if (clientSnap && clientSnap.exists()) return "client";
    } catch (e) {
      console.warn("[Firestore Sync] Role fallback doc check error:", e.message || e);
    }

    return null;
  }

  function getProviderApprovalState(data = {}) {
    const status = String(data.subscriptionStatus || data.accountStatus || data.verificationStatus || "pending").toLowerCase();
    const accountStatus = String(data.accountStatus || "").toLowerCase();
    const verificationStatus = String(data.verificationStatus || "").toLowerCase();
    const isExplicitlyRejected = status === "rejected" || accountStatus === "rejected" || verificationStatus === "rejected";
    const isExplicitlySuspended = status === "suspended" || accountStatus === "suspended";
    const approved = (status === "approved" || status === "active" || accountStatus === "active") &&
      !isExplicitlyRejected &&
      !isExplicitlySuspended;

    return { approved, isExplicitlyRejected, isExplicitlySuspended };
  }

  function providerMatchesAuthUser(provider, docId, user) {
    const authUid = String(user && user.uid || "").trim();
    const authEmail = String(user && user.email || "").trim().toLowerCase();
    const providerUid = String((provider && (provider.uid || provider.id)) || docId || "").trim();
    const providerEmail = String((provider && (provider.email || provider.providerEmail || provider.ownerEmail)) || "").trim().toLowerCase();
    return (authUid && providerUid === authUid) || (authEmail && providerEmail === authEmail);
  }

  async function findProviderDocForAuthUser(user) {
    if (!user || !user.uid) return null;

    const candidates = [];
    const authEmail = String(user.email || "").trim().toLowerCase();

    async function addDocSnapshot(snap) {
      if (snap && snap.exists()) {
        candidates.push({ docId: snap.id, data: snap.data() || {} });
      }
    }

    async function addEmailQuery(fieldName) {
      if (!authEmail) return;
      const qs = await getDocs(query(collection(firebaseDb, "providers"), where(fieldName, "==", authEmail)));
      qs.forEach((docSnap) => {
        candidates.push({ docId: docSnap.id, data: docSnap.data() || {} });
      });
    }

    await addDocSnapshot(await getDoc(doc(firebaseDb, "providers", String(user.uid))).catch(() => null));
    await addEmailQuery("email");
    await addEmailQuery("providerEmail");
    await addEmailQuery("ownerEmail");

    const matchingCandidates = candidates.filter((candidate) => providerMatchesAuthUser(candidate.data, candidate.docId, user));
    const approvedCandidate = matchingCandidates.find((candidate) => getProviderApprovalState(candidate.data).approved);
    const selected = approvedCandidate || matchingCandidates[0];

    if (selected) {
      return {
        docId: selected.docId,
        data: selected.data,
        ...getProviderApprovalState(selected.data),
      };
    }

    return null;
  }

  async function rejectPendingProviderLogin(user) {
    const idTokenResult = await user.getIdTokenResult(true);
    const claims = (idTokenResult && idTokenResult.claims) || {};
    const tokenRole = String(claims.role || "").toLowerCase();
    const approved = claims.approved === true || claims.providerApproved === true;

    if (tokenRole === "provider" && approved) {
      return;
    }

    if (tokenRole === "client" || tokenRole === "admin" || claims.admin === true) {
      return;
    }

    try {
      const projectId = window.firebaseConfig?.projectId || "what-to-eat-e5ce9";
      const appCheckToken = await getRequiredAppCheckToken();
      const response = await fetch(`https://us-central1-${projectId}.cloudfunctions.net/getProviderLoginStatus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Firebase-AppCheck": appCheckToken,
          Authorization: "Bearer " + (await user.getIdToken()),
        },
        body: JSON.stringify({}),
      });
      const result = await response.json().catch(() => null);
      if (response.ok && result && result.ok === true) {
        if (result.provider === true && result.approved === true) {
          await user.getIdTokenResult(true).catch(() => {});
          return;
        }
        if (result.provider === true && result.approved !== true) {
          const providerMatch = await findProviderDocForAuthUser(user).catch((e) => {
            console.warn("[Firestore Sync] Provider identity fallback failed:", e.message || e);
            return null;
          });
          if (providerMatch && providerMatch.approved) {
            return;
          }
          await authSignOut(firebaseAuth).catch(() => {});
          throw new Error("Your provider account is pending Super Admin approval. You will be able to log in after approval.");
        }
        if (result.provider === false) {
          return;
        }
      }
    } catch (e) {
      if (e.message && e.message.includes("pending Super Admin approval")) {
        throw e;
      }
      console.warn("[Firestore Sync] getProviderLoginStatus call failed, checking Firestore document:", e.message || e);
    }

    try {
      const providerMatch = await findProviderDocForAuthUser(user);
      if (providerMatch) {
        if (providerMatch.approved) return;
        if (providerMatch.isExplicitlyRejected) {
          await authSignOut(firebaseAuth).catch(() => {});
          throw new Error("Your provider account registration was rejected. Please contact support.");
        }
        if (providerMatch.isExplicitlySuspended) {
          await authSignOut(firebaseAuth).catch(() => {});
          throw new Error("Your provider account has been suspended. Please contact support.");
        }
        await authSignOut(firebaseAuth).catch(() => {});
        throw new Error("Your provider account is pending Super Admin approval. You will be able to log in after approval.");
      }
    } catch (e) {
      if (e.message && (e.message.includes("pending Super Admin approval") || e.message.includes("rejected") || e.message.includes("suspended"))) {
        throw e;
      }
      console.warn("[Firestore Sync] Fallback provider doc check failed:", e.message || e);
    }
  }

  async function getUserRole(uid) {
    currentProviderDoc = null;
    const authUser = firebaseAuth.currentUser;
    if (!authUser) {
      throw new Error("User not signed in");
    }

    const trustedRole = await resolveServerRoleForCurrentUser(authUser);
    if (trustedRole && trustedRole !== "admin") {
      const collectionName = `${trustedRole}s`;
      const userRef = doc(firebaseDb, collectionName, String(uid));
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        throw new Error(`Authorized ${trustedRole} role is missing its Firestore profile.`);
      }

      let data = userSnap.data() || {};
      const docRole = String(data.role || "").toLowerCase();
      if (docRole && docRole !== trustedRole) {
        throw new Error(`Role mismatch: token role ${trustedRole} does not match Firestore role ${docRole}.`);
      }

      if (trustedRole === "provider") {
        let approvalState = getProviderApprovalState(data);

        if (!approvalState.approved) {
          const providerMatch = await findProviderDocForAuthUser(authUser);
          if (providerMatch && providerMatch.approved) {
            data = providerMatch.data;
            approvalState = providerMatch;
          }
        }

        if (!approvalState.approved) {
          throw new Error("Provider account is not approved for active provider access.");
        }
      }

      currentUser = authUser;
      userRole = trustedRole;
      if (trustedRole === "provider") {
        currentProviderDoc = data;
      }
      startUserSync(trustedRole);
      return { role: trustedRole, data };
    }

    const authEmail = authUser && authUser.email ? String(authUser.email).toLowerCase() : null;

    // Retry logic: try up to 3 times with delay to allow custom claims to propagate
    const MAX_RETRIES = 3;
    let lastError = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        // Fast path: if document id equals uid
        try {
          const clientRef = doc(firebaseDb, "clients", String(uid));
          const clientSnap = await getDoc(clientRef);
          if (clientSnap.exists()) {
            currentUser = authUser;
            userRole = "client";
            startUserSync("client");
            return { role: "client", data: clientSnap.data() };
          }
        } catch (e) {
          lastError = e;
          // Permission error may occur if claims haven't propagated yet
          if (e.code === "permission-denied" && attempt < MAX_RETRIES) {
            console.log(`[Firestore Sync] getUserRole attempt ${attempt} failed (permission-denied), retrying...`);
            await new Promise(r => setTimeout(r, 1000 * attempt));
            continue;
          }
        }

        try {
          const providerRef = doc(firebaseDb, "providers", String(uid));
          const providerSnap = await getDoc(providerRef);
          if (providerSnap.exists()) {
            currentUser = authUser;
            userRole = "provider";
            currentProviderDoc = providerSnap.data() || null;
            startUserSync("provider");
            return { role: "provider", data: providerSnap.data() };
          }
        } catch (e) {
          lastError = e;
          if (e.code === "permission-denied" && attempt < MAX_RETRIES) {
            console.log(`[Firestore Sync] getUserRole attempt ${attempt} failed (permission-denied), retrying...`);
            await new Promise(r => setTimeout(r, 1000 * attempt));
            continue;
          }
        }

    // If we get here without permission errors, continue to email-based lookups
    break;
      } catch (e) {
        lastError = e;
        if (attempt < MAX_RETRIES) {
          console.log(`[Firestore Sync] getUserRole attempt ${attempt} failed, retrying...`);
          await new Promise(r => setTimeout(r, 1000 * attempt));
        }
      }
    }

    // Provider/client docs in your Firestore appear to use email fields,
    // while providerId is numeric and not equal to auth uid.
    if (!authEmail) {
      console.warn("[Firestore Sync] No auth email available, cannot lookup user");
      throw new Error("User data not found");
    }

    // Try clients by email
    try {
      const clientQ = query(collection(firebaseDb, "clients"), where("email", "==", authEmail));
      const clientQs = await getDocs(clientQ);
      if (!clientQs.empty) {
        const d = clientQs.docs[0];
        currentUser = authUser;
        userRole = "client";
        startUserSync("client");
        return { role: "client", data: d.data() };
      }
    } catch (e) {
      console.warn("[Firestore Sync] Client email lookup failed:", e.message);
    }

    // Try providers by email
    try {
      const providerQ = query(collection(firebaseDb, "providers"), where("email", "==", authEmail));
      const providerQs = await getDocs(providerQ);
      if (!providerQs.empty) {
        const d = providerQs.docs[0];
        currentUser = authUser;
        userRole = "provider";
        currentProviderDoc = d.data() || null;
        startUserSync("provider");
        return { role: "provider", data: d.data() };
      }
    } catch (e) {
      console.warn("[Firestore Sync] Provider email lookup failed:", e.message);
    }

    // Some docs may store providerEmail instead of email
    try {
      const providerEmailQ = query(collection(firebaseDb, "providers"), where("providerEmail", "==", authEmail));
      const providerEmailQs = await getDocs(providerEmailQ);
      if (!providerEmailQs.empty) {
        const d = providerEmailQs.docs[0];
        currentUser = authUser;
        userRole = "provider";
        currentProviderDoc = d.data() || null;
        startUserSync("provider");
        return { role: "provider", data: d.data() };
      }
    } catch (e) {
      console.warn("[Firestore Sync] ProviderEmail lookup failed:", e.message);
    }

    // Try ownerEmail field as another variant
    try {
      const providerOwnerEmailQ = query(collection(firebaseDb, "providers"), where("ownerEmail", "==", authEmail));
      const providerOwnerEmailQs = await getDocs(providerOwnerEmailQ);
      if (!providerOwnerEmailQs.empty) {
        const d = providerOwnerEmailQs.docs[0];
        currentUser = authUser;
        userRole = "provider";
        currentProviderDoc = d.data() || null;
        startUserSync("provider");
        return { role: "provider", data: d.data() };
      }
    } catch (e) {
      console.warn("[Firestore Sync] OwnerEmail lookup failed:", e.message);
    }

    // If we still can't find the user, do NOT auto-create a client doc.
    // The user may be a provider whose doc hasn't been created yet (e.g. during registration),
    // or the Firestore rules may be blocking reads. Instead, return null so the caller
    // can handle the missing data appropriately.
    console.warn("[Firestore Sync] User not found in Firestore (neither clients nor providers). uid:", uid, "email:", authEmail);

    // Check if the user might be a provider by trying to read the providers/{uid} doc directly
    // (This handles the case where the provider doc was just created but the email query fails)
    try {
      const providerRef = doc(firebaseDb, "providers", String(uid));
      const providerSnap = await getDoc(providerRef);
      if (providerSnap.exists()) {
        const data = providerSnap.data() || {};
        currentUser = authUser;
        userRole = "provider";
        currentProviderDoc = data;
        startUserSync("provider");
        return { role: "provider", data: data };
      }
    } catch (e) {
      console.warn("[Firestore Sync] Provider doc lookup failed:", e.message);
    }

    throw new Error("User data not found");
  }

  const DISPOSABLE_EMAIL_DOMAINS = new Set([
    "mailinator.com", "10minutemail.com", "guerrillamail.com", "tempmail.com", "yopmail.com",
    "trashmail.com", "dispostable.com", "sharklasers.com", "getnada.com", "throwawaymail.com",
    "fakeinbox.com", "crazymailing.com", "mytemp.email", "temp-mail.org", "dropmail.me",
    "generator.email", "maildrop.cc", "getairmail.com", "disposablemail.com", "0815.ru",
    "anonbox.net", "binkmail.com", "bobmail.info", "chacuo.net", "dayrep.com", "disposable.com",
    "emailondeck.com", "emailsensei.com", "filzmail.com", "gishpuppy.com", "hmamail.com",
    "incognitomail.org", "mailasia.com", "mailcatch.com", "mailnesia.com", "mailnull.com",
    "mohmal.com", "my10minutemail.com", "oneoffemail.com", "soodonims.com", "spambog.com",
    "spambox.us", "spamgourmet.com", "tempinbox.com", "trashmail.net", "zippymail.in", "temp-mail.com",
    "guerrillamailblock.com", "grr.la", "guerrillamail.biz", "guerrillamail.de"
  ]);

  const FAKE_TEST_DOMAINS = new Set([
    "example.com", "example.org", "example.net", "test.com", "test.org",
    "invalid.com", "local.host", "localhost", "domain.com", "email.com"
  ]);

  function validateRealEmail(email) {
    if (!email || typeof email !== "string") {
      throw new Error("A valid email address is required.");
    }
    const clean = email.trim().toLowerCase();

    // Strict RFC 5322 regex
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    if (!emailRegex.test(clean)) {
      throw new Error("Please enter a valid email address format (e.g. name@domain.com).");
    }

    const parts = clean.split("@");
    if (parts.length !== 2) throw new Error("Invalid email address format.");
    const [local, domain] = parts;

    if (local.length < 1 || local.length > 64) {
      throw new Error("Email local part length must be between 1 and 64 characters.");
    }

    const domainParts = domain.split(".");
    const tld = domainParts[domainParts.length - 1];
    if (!tld || tld.length < 2) {
      throw new Error("Email domain top-level extension must be at least 2 characters (e.g. .com, .org).");
    }

    if (DISPOSABLE_EMAIL_DOMAINS.has(domain)) {
      throw new Error("Disposable and temporary email services (e.g. mailinator, tempmail) are strictly prohibited. Please sign up using a genuine, active email address.");
    }

    if (FAKE_TEST_DOMAINS.has(domain)) {
      throw new Error("Please sign up with a real, operational email address.");
    }

    // Reject spammy local parts
    if (/^(.)\1{4,}@/.test(clean) || /^(asdf|qwerty|123456)/i.test(local)) {
      throw new Error("Please use a genuine email address to sign up.");
    }

    return clean;
  }
  window.validateRealEmail = validateRealEmail;

  async function getRequiredAppCheckToken() {
    if (typeof window.getFirebaseAppCheckToken !== "function") {
      throw new Error("Firebase App Check is unavailable. Refresh and try again.");
    }
    const result = await window.getFirebaseAppCheckToken();
    if (!result || !result.token) {
      throw new Error("Firebase App Check did not return a token.");
    }
    return result.token;
  }

  async function signUp(email, password, role, userData) {
    if (!(await ensureFirebaseServicesReady())) {
      throw new Error("Firebase not ready");
    }

    const cleanEmail = validateRealEmail(email);

    const userCredential = await createUserWithEmailAndPassword(firebaseAuth, cleanEmail, password);
    const user = userCredential.user;

    if (role === "provider") {
      // Provider registration is a two-step backend process:
      //   1. Firebase Auth user is created above (client-side is unavoidable).
      //   2. The `registerProvider` Cloud Function creates the Firestore doc and
      //      sets initial claims { role: 'provider', approved: false }.
      //
      // The provider is then signed out immediately so the unapproved token is
      // not cached in the browser.  They must wait for admin approval before
      // they can log in as an active provider.

      if (!user.emailVerified) {
        await sendEmailVerification(user).catch((e) => {
          console.warn("[ProviderRegister] Failed to send verification email:", e && e.message ? e.message : e);
        });
      }

      // Call registerProvider CF — it writes the Firestore doc + sets claims.
      const PROJECT_ID = window.firebaseConfig?.projectId || "what-to-eat-e5ce9";
      const FUNCTIONS_REGION = "us-central1";
      const cfUrl = `https://${FUNCTIONS_REGION}-${PROJECT_ID}.cloudfunctions.net/registerProvider`;

      let cfError = null;
      try {
        const token = await user.getIdToken();
        const appCheckToken = await getRequiredAppCheckToken();
        const cfRes = await fetch(cfUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Firebase-AppCheck": appCheckToken,
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: userData?.name || "",
            phone: userData?.phone || "",
            country: userData?.country || "",
            type: userData?.type || "Restaurant",
            address: userData?.address || "",
            addressComponents: userData?.addressComponents || {},
            acceptsCustomRequests: userData?.acceptsCustomRequests || false,
          }),
        });

        if (!cfRes.ok) {
          const text = await cfRes.text().catch(() => "");
          cfError = `Registration backend returned ${cfRes.status}: ${text}`;
        }
      } catch (e) {
        cfError = e && e.message ? e.message : String(e);
      }

      // Always sign out the provider after registration so the unapproved
      // session token is not left active in the browser.
      await authSignOut(firebaseAuth).catch(() => {});

      if (cfError) {
        console.error("[ProviderRegister] registerProvider CF failed:", cfError);
        throw new Error("Provider registration could not be completed. Please contact support.");
      }

      throw new Error(
        "Provider registration requires email verification and admin approval. " +
        "We sent a verification email; please verify it. " +
        "You will be notified once your account has been approved."
      );
    }

    // ── Client registration (unchanged) ──────────────────────────────────────
    const userDocData = {
      uid: user.uid,
      email: cleanEmail,
      role: role,
      name: userData?.name || "",
      phone: userData?.phone || "",
      country: userData?.country || "",
      createdAt: serverTimestamp()
    };

    const collectionName = role + "s";
    const userRef = doc(firebaseDb, collectionName, user.uid);
    await setDoc(userRef, userDocData);

    currentUser = user;
    userRole = role;

    startUserSync(role);
    return user;
  }

  async function signIn(email, password) {
    if (!(await ensureFirebaseServicesReady())) {
      throw new Error("Firebase not ready");
    }

    const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);
    const user = userCredential.user;
    await rejectPendingProviderLogin(user);
    const trustedRole = await resolveServerRoleForCurrentUser(user);

    if (!trustedRole || trustedRole === "admin") {
      throw new Error("This account is not authorized as a client or provider. Use the admin login flow for admin access.");
    }

    if (trustedRole === "provider") {
      if (typeof user.reload === "function") {
        await user.reload().catch(() => {});
      }
      if (user.emailVerified !== true) {
        await authSignOut(firebaseAuth).catch(() => {});
        throw new Error("Please verify your email before provider login.");
      }
    }

    console.log("[Firebase Auth] User signed in:", user.uid, "with trusted role:", trustedRole);
    return getUserRole(user.uid);
  }


  // Ensures the `client` role custom claim is set by calling the setUserRole
  // Cloud Function when needed.  Provider and admin claims are NEVER set from
  // the client side — providers receive their claims via registerProvider +
  // approveProvider Cloud Functions (both admin-gated).
  async function ensureUserRoleClaim(role) {
    if (!currentUser || !currentUser.uid) return;

    // Only the client role can be self-asserted from the browser.
    // Provider claims require admin approval and are set server-side only.
    if (role !== "client") {
      if (role === "provider") {
        console.log("[Firestore Sync] Provider role claim is managed server-side only. Skipping client-side claim sync.");
      }
      return;
    }

    // Refresh token and check whether the claim is already correct.
    try {
      const idTokenResult = await currentUser.getIdTokenResult(true);
      const customClaims = idTokenResult.claims || {};

      // Never overwrite admin claims.
      if (customClaims.admin === true) {
        console.log("[Firestore Sync] Admin user detected, skipping role claim sync");
        return;
      }

      if (customClaims.role === role) {
        console.log("[Firestore Sync] Role claim already set to", role, "- skipping sync");
        return;
      }

      console.log("[Firestore Sync] Current role claim is", customClaims.role, "- will update to", role);
    } catch (e) {
      console.warn("[Firestore Sync] Token refresh failed; will attempt role sync via HTTP:", e.message);
    }

    // Call the Cloud Function to set the client role claim.
    const PROJECT_ID = window.firebaseConfig?.projectId || "what-to-eat-e5ce9";
    const FUNCTIONS_REGION = "us-central1";
    const url = `https://${FUNCTIONS_REGION}-${PROJECT_ID}.cloudfunctions.net/setUserRole`;

    try {
      const token = await currentUser.getIdToken();
      const appCheckToken = await getRequiredAppCheckToken();
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Firebase-AppCheck": appCheckToken,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          uid: currentUser.uid,
          role: role
        })
      });

      if (!res.ok) {
        const text = await res.text();
        console.warn("[Firestore Sync] setUserRole HTTP failed:", res.status, text);
        return;
      }

      const result = await res.json();
      console.log("[Firestore Sync] setUserRole success:", result);

      // Force refresh the token to pick up the new claims.
      try {
        await currentUser.getIdTokenResult(true);
        console.log("[Firestore Sync] Token refreshed with new claims");
      } catch (e) {
        console.warn("[Firestore Sync] Token refresh after claim set failed:", e.message);
      }
    } catch (e) {
      console.warn("[Firestore Sync] Failed to call setUserRole:", e.message);
    }
  }


  // ==================== MODULAR ADMIN/LOOKUP HELPERS ====================
  // These are used by legacy UI code in app.js for bootstrap + login lookups.
  // Keep them modular Firestore-only to avoid mixed API crashes.

  function toClientDocShape(d) {
    const docData = d || {};
    return {
      id: docData.id ?? docData.uid ?? d?.id,
      uid: docData.uid ?? d?.id,
      name: docData.name,
      phone: docData.phone,
      email: docData.email,
      country: docData.country,
      subscriptionPlan: docData.subscriptionPlan,
      subscriptionStatus: docData.subscriptionStatus,
      subscriptionStart: docData.subscriptionStart,
      createdAt: docData.createdAt
    };
  }

  function toProviderDocShape(d) {
    const docData = d || {};
    return {
      id: docData.id ?? docData.uid ?? d?.id,
      uid: docData.uid ?? d?.id,
      name: docData.name,
      phone: docData.phone,
      email: docData.email,
      type: docData.type,
      address: docData.address,
      country: docData.country,
      acceptsCustomRequests: docData.acceptsCustomRequests,
      subscriptionStatus: docData.subscriptionStatus,
      subscriptionPlan: docData.subscriptionPlan,
      subscriptionEnd: docData.subscriptionEnd,
      subscriptionApprovedBy: docData.subscriptionApprovedBy,
      subscriptionApprovedAt: docData.subscriptionApprovedAt,
      subscriptionRejectionReason: docData.subscriptionRejectionReason,
      subscriptionSuspendedReason: docData.subscriptionSuspendedReason,
      subscriptionNotes: docData.subscriptionNotes,
      createdAt: docData.createdAt
    };
  }

  async function getClientByUid(uid) {
    if (!firebaseReady || !firebaseDb) return null;
    const ref = doc(firebaseDb, "clients", String(uid));
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data() || {};
    return { id: data.id ?? data.uid ?? snap.id, uid: data.uid ?? snap.id, ...data };
  }

  async function getClientByEmail(email) {
    if (!firebaseReady || !firebaseDb) return null;
    const q = query(collection(firebaseDb, "clients"), where("email", "==", String(email).toLowerCase()));
    const snaps = await getDocs(q);
    if (snaps.empty) return null;
    const d = snaps.docs[0];
    const data = d.data() || {};
    return { id: data.id ?? data.uid ?? d.id, uid: data.uid ?? d.id, ...data };
  }

  async function upsertClientFromAuth(uid, email, nameGuess) {
    if (!firebaseReady || !firebaseDb) throw new Error("Firebase not ready");
    const ref = doc(firebaseDb, "clients", String(uid));
    const existing = await getDoc(ref);

    const payload = {
      uid,
      id: uid,
      email: String(email).toLowerCase(),
      name: nameGuess || email.split("@")[0],
      phone: existing.exists() ? (existing.data().phone ?? "") : "",
      country: existing.exists() ? (existing.data().country ?? "") : "",
      subscriptionPlan: existing.exists() ? existing.data().subscriptionPlan : "free",
      subscriptionStatus: existing.exists() ? existing.data().subscriptionStatus : "active",
      subscriptionStart: existing.exists() ? existing.data().subscriptionStart : new Date().toISOString(),
      createdAt: existing.exists() ? existing.data().createdAt : serverTimestamp()
    };

    await setDoc(ref, payload, { merge: true });
    const snap = await getDoc(ref);
    return snap.data() || payload;
  }

  async function getProviderByUid(uid) {
    if (!firebaseReady || !firebaseDb) return null;

    const uidStr = String(uid);

    // Fast path (doc id == uid)
    try {
      const ref = doc(firebaseDb, "providers", uidStr);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() || {};
        return { id: data.id ?? data.uid ?? snap.id, uid: data.uid ?? snap.id, ...data };
      }
    } catch (e) {
      console.warn("[Firebase Integration] getProviderByUid fast-path failed:", e);
    }

    // Fallback #1: providers stored under different IDs but contain a `uid` field
    try {
      const providerQ = query(collection(firebaseDb, "providers"), where("uid", "==", uidStr));
      const providerQs = await getDocs(providerQ);
      if (!providerQs.empty) {
        const d = providerQs.docs[0];
        const data = d.data() || {};
        return { id: data.id ?? data.uid ?? d.id, uid: data.uid ?? d.id, ...data };
      }
    } catch (e) {
      // ignore, may fail if uid field doesn't exist
    }

    // Fallback #2: some provider docs may store the auth uid in `providerId`
    try {
      const providerIdQ = query(collection(firebaseDb, "providers"), where("providerId", "==", uidStr));
      const providerIdQs = await getDocs(providerIdQ);
      if (!providerIdQs.empty) {
        const d = providerIdQs.docs[0];
        const data = d.data() || {};
        return { id: data.id ?? data.uid ?? d.id, uid: uidStr, ...data };
      }
    } catch (e) {
      // ignore
    }

    // Fallback #3: some provider docs may store a legacy numeric/string `id` equal to uidStr
    try {
      const idQ = query(collection(firebaseDb, "providers"), where("id", "==", uidStr));
      const idQs = await getDocs(idQ);
      if (!idQs.empty) {
        const d = idQs.docs[0];
        const data = d.data() || {};
        return { id: data.id ?? data.uid ?? d.id, uid: uidStr, ...data };
      }
    } catch (e) {
      // ignore
    }

    // Fallback #4: use email as reliable lookup
    const currentEmail = firebaseAuth?.currentUser?.email;
    if (currentEmail) {
      const emailStr = String(currentEmail).toLowerCase();
      try {
        const providerEmailQ = query(collection(firebaseDb, "providers"), where("email", "==", emailStr));
        const providerEmailQs = await getDocs(providerEmailQ);
        if (!providerEmailQs.empty) {
          const d = providerEmailQs.docs[0];
          const data = d.data() || {};
          // Ensure we always attach the firebaseAuth uid for consistent session matching.
          return { id: data.id ?? data.uid ?? d.id, uid: uidStr, ...data };
        }
      } catch (e) {
        // ignore
      }
    }

    return null;
  }

  async function getProviderByEmail(email) {
    if (!firebaseReady || !firebaseDb) return null;
    const q = query(collection(firebaseDb, "providers"), where("email", "==", String(email).toLowerCase()));
    const snaps = await getDocs(q);
    if (snaps.empty) return null;
    const d = snaps.docs[0];
    const data = d.data() || {};
    return { id: data.id ?? data.uid ?? d.id, uid: data.uid ?? d.id, ...data };
  }

  async function listClients() {
    if (!firebaseReady || !firebaseDb) return [];
    const snaps = await getDocs(collection(firebaseDb, "clients"));
    const out = [];
    snaps.forEach((d) => {
      const data = d.data() || {};
      out.push({
        id: data.id ?? data.uid ?? d.id,
        uid: data.uid ?? d.id,
        name: data.name,
        phone: data.phone,
        email: data.email,
        country: data.country,
        subscriptionPlan: data.subscriptionPlan,
        subscriptionStatus: data.subscriptionStatus,
        subscriptionStart: data.subscriptionStart,
        createdAt: data.createdAt
      });
    });
    return out;
  }

  async function listProviders() {
    if (!firebaseReady || !firebaseDb) return [];
    const snaps = await getDocs(collection(firebaseDb, "providers"));
    const out = [];
    snaps.forEach((d) => {
      const data = d.data() || {};
      out.push({
        id: data.id ?? data.uid ?? d.id,
        uid: data.uid ?? d.id,
        name: data.name,
        phone: data.phone,
        email: data.email,
        type: data.type,
        address: data.address,
        country: data.country,
        acceptsCustomRequests: data.acceptsCustomRequests,
        subscriptionStatus: data.subscriptionStatus,
        subscriptionPlan: data.subscriptionPlan,
        subscriptionEnd: data.subscriptionEnd,
        subscriptionApprovedBy: data.subscriptionApprovedBy,
        subscriptionApprovedAt: data.subscriptionApprovedAt,
        subscriptionRejectionReason: data.subscriptionRejectionReason,
        subscriptionSuspendedReason: data.subscriptionSuspendedReason,
        subscriptionNotes: data.subscriptionNotes,
        verificationStatus: data.verificationStatus || 'pending',
        accountStatus: data.accountStatus || 'pending',
        payoutStatus: data.payoutStatus || 'pending',
        createdAt: data.createdAt
      });
    });
    return out;
  }

  async function listReviews() {
    if (!firebaseReady || !firebaseDb) return [];
    const snaps = await getDocs(collection(firebaseDb, "reviews"));
    const out = [];
    snaps.forEach((d) => {
      const data = d.data() || {};
      out.push({
        id: data.id ?? d.id,
        providerId: data.providerId,
        providerName: data.providerName,
        clientId: data.clientId,
        clientEmail: data.clientEmail,
        clientName: data.clientName,
        rating: data.rating,
        comment: data.comment,
        orderId: data.orderId,
        createdAt: data.createdAt
      });
    });
    return out;
  }

  async function signOut() {
    if (!(await ensureFirebaseServicesReady())) return;

    // ============ FIX: Stop admin real-time listeners on logout ============
    // When the admin logs out, the onSnapshot listeners on clients and providers
    // collections continue running but the user is no longer authenticated.
    // This causes "Missing or insufficient permissions" errors.
    // We must explicitly stop these listeners before stopping all other listeners.
    stopAdminRealTimeListeners();
    if (typeof window.__stopFirestoreRealtimeSync === "function") {
      window.__stopFirestoreRealtimeSync();
    }
    // ============ END FIX ============

    stopAllListeners();
    await authSignOut(firebaseAuth);

    console.log("[Firebase Auth] Signed out");
    currentUser = null;
    userRole = null;
  }

  function deriveOrderCompliance(orderData = {}) {
    const policyBuilder = window.marketComplianceService && typeof window.marketComplianceService.buildMarketCompliancePolicy === 'function'
      ? window.marketComplianceService.buildMarketCompliancePolicy
      : fallbackMarketComplianceService.buildMarketCompliancePolicy;
    return policyBuilder(orderData || {});
  }

  async function saveOrder(orderData) {
    if (!currentUser || userRole !== "client") {
      throw new Error("Must be logged in as client");
    }
    if (orderData.paymentMethod && orderData.paymentMethod !== "cash") {
      throw new Error("Online card payments are unavailable. St. Vincent and the Grenadines orders must be paid in cash.");
    }

    const rawProviderId = orderData ? orderData.providerId : null;
    const providerId = rawProviderId == null ? null : String(rawProviderId);

    if (providerId == null || String(providerId).trim() === "") {
      throw new Error("Cannot save order: providerId missing/invalid");
    }

    const compliance = orderData.compliance && typeof orderData.compliance === "object"
      ? orderData.compliance
      : deriveOrderCompliance(orderData);

    const validation = (window.marketComplianceService && typeof window.marketComplianceService.validateMarketCompliancePayload === 'function')
      ? window.marketComplianceService.validateMarketCompliancePayload({ ...orderData, compliance })
      : { valid: true, errors: [] };

    if (!validation.valid) {
      throw new Error(validation.errors.join('; '));
    }

    const orderDoc = {
      id: orderData.id || Date.now(),
      clientId: currentUser.uid,
      clientEmail: currentUser.email,
      clientCountry: orderData.clientCountry || compliance.country || "US",
      providerId: providerId,
      providerName: orderData.providerName,
      providerCountry: orderData.providerCountry || "US",
      menuItemId: String(orderData.menuItemId || ""),
      items: Array.isArray(orderData.items) ? orderData.items : [],
      foodItem: orderData.foodItem,
      portion: orderData.portion,
      quantity: Number(orderData.quantity || 1),
      unitPrice: Number(orderData.unitPrice || 0),
      drink: orderData.drink || "",
      drinkPrice: Number(orderData.drinkPrice || 0),
      drinkQuantity: Number(orderData.drinkQuantity || 0),
      price: orderData.price,
      taxRate: Number(orderData.taxRate ?? compliance.taxRate ?? 0),
      taxAmount: Number(orderData.taxAmount ?? compliance.taxAmount ?? 0),
      currency: orderData.currency || compliance.currency || "USD",
      refundWindowHours: Number(orderData.refundWindowHours ?? compliance.refundWindowHours ?? 24),
      cancellationWindowHours: Number(orderData.cancellationWindowHours ?? compliance.cancellationWindowHours ?? 2),
      payoutDelayDays: Number(orderData.payoutDelayDays ?? compliance.payoutDelayDays ?? 7),
      payoutFeePercent: Number(orderData.payoutFeePercent ?? compliance.payoutFeePercent ?? 0.05),
      payoutFeeAmount: Number(orderData.payoutFeeAmount ?? compliance.payoutFeeAmount ?? 0),
      refundPolicy: orderData.refundPolicy || compliance.refundPolicy || "Refunds follow the target-market policy.",
      cancellationPolicy: orderData.cancellationPolicy || compliance.cancellationPolicy || "Cancellations follow the target-market policy.",
      payoutPolicy: orderData.payoutPolicy || compliance.payoutPolicy || "Provider payouts follow the target-market policy.",
      compliance,
      orderType: orderData.orderType,
      message: orderData.message,
      paymentMethod: "cash",
      paymentStatus: "unpaid",
      stripePaymentIntentId: "",
      status: "pending",
      statusHistory: [
        {
          status: "pending",
          timestamp: new Date().toISOString(),
          note: "Order placed"
        }
      ],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(firebaseDb, "orders", String(orderDoc.id)), orderDoc);
    console.log("[Firestore Sync] Order saved:", orderDoc.id, "providerId:", providerId);

    // Automatically create a transaction record for this order
    try {
      await saveTransaction({
        orderId: orderDoc.id,
        clientId: orderDoc.clientId,
        clientEmail: orderDoc.clientEmail,
        providerId: orderDoc.providerId,
        providerName: orderDoc.providerName,
        foodItem: orderDoc.foodItem,
        amount: orderDoc.price || 0,
        orderType: orderDoc.orderType,
        status: "pending"
      });
    } catch (txErr) {
      console.warn("[Firestore Sync] Failed to create transaction record:", txErr.message);
    }

    return orderDoc;
  }

  /**
   * Save a transaction record to Firestore.
   * Creates a financial transaction entry linked to an order.
   *
   * @param {Object} transactionData
   * @param {string} transactionData.orderId - The order ID this transaction belongs to
   * @param {string} transactionData.clientId - Client Firebase Auth UID
   * @param {string} transactionData.clientEmail - Client email
   * @param {string} transactionData.providerId - Provider Firebase Auth UID
   * @param {string} transactionData.providerName - Provider/business name
   * @param {string} transactionData.foodItem - Food item ordered
   * @param {number} transactionData.amount - Transaction amount
   * @param {string} transactionData.orderType - 'pickup' or 'delivery'
   * @param {string} [transactionData.status] - 'pending' | 'completed' | 'refunded' | 'cancelled'
   * @param {string} [transactionData.paymentMethod] - 'cash' | 'card' | 'online' | 'other'
   * @returns {Promise<Object>} The saved transaction document
   */
  async function saveTransaction(transactionData) {
    if (!firebaseReady || !firebaseDb) throw new Error("Firebase not ready");

    const transactionDoc = {
      id: `tx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      orderId: transactionData.orderId,
      clientId: transactionData.clientId,
      clientEmail: transactionData.clientEmail,
      clientName: transactionData.clientName || "",
      providerId: transactionData.providerId,
      providerName: transactionData.providerName || "",
      foodItem: transactionData.foodItem || "",
      amount: Number(transactionData.amount) || 0,
      orderType: transactionData.orderType || "pickup",
      paymentMethod: transactionData.paymentMethod || "cash",
      status: transactionData.status || "pending",
      statusHistory: [
        {
          status: transactionData.status || "pending",
          timestamp: new Date().toISOString(),
          note: "Transaction created"
        }
      ],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(firebaseDb, "transactions", transactionDoc.id), transactionDoc);
    console.log("[Firestore Sync] Transaction saved:", transactionDoc.id, "amount:", transactionDoc.amount);
    return transactionDoc;
  }

  /**
   * Update a transaction record (e.g. mark as completed/refunded).
   *
   * @param {string} transactionId - The transaction document ID
   * @param {Object} updates - Fields to update (status, paymentMethod, etc.)
   * @returns {Promise<void>}
   */
  async function updateTransaction(transactionId, updates) {
    if (!firebaseReady || !firebaseDb) throw new Error("Firebase not ready");

    const txRef = doc(firebaseDb, "transactions", String(transactionId));
    const snap = await getDoc(txRef);
    if (!snap.exists()) throw new Error("Transaction not found");

    const updatePayload = {
      ...updates,
      updatedAt: serverTimestamp()
    };

    // Append to status history if status changed
    if (updates.status) {
      updatePayload.statusHistory = arrayUnion({
        status: updates.status,
        timestamp: new Date().toISOString(),
        note: updates.note || "Status changed to " + updates.status
      });
    }

    await updateDoc(txRef, updatePayload);
    console.log("[Firestore Sync] Transaction updated:", transactionId, updates);
  }

  async function deleteMenuItem(menuItemId) {
    if (!firebaseReady || !firebaseDb) throw new Error("Firebase not ready");
    await deleteDoc(doc(firebaseDb, "menuItems", String(menuItemId)));
    console.log("[Firestore Sync] Menu item deleted:", menuItemId);
  }

  /**
   * Upload a base64 image to Firebase Storage and return the download URL.
   * Used for menu item photos. Stores the image in Storage instead of
   * embedding a base64 string directly in Firestore (which exceeds
   * Firestore's 1MB document limit for large images).
   *
   * @param {string} base64Data - Base64 data URI of the image
   * @param {string} providerUid - Provider Firebase Auth UID
   * @param {string} [menuItemId] - Menu item ID
   * @returns {Promise<string>} Download URL for the uploaded image
   */
  async function uploadPhotoToStorage(base64Data, providerUid, menuItemId) {
    if (!currentUser || userRole !== "provider") {
      throw new Error("Must be logged in as provider");
    }

    // Use the storage integration module if available
    if (typeof window.uploadMenuPhotoToStorage === "function") {
      const downloadURL = await window.uploadMenuPhotoToStorage(base64Data, providerUid, menuItemId);
      console.log("[Firestore Sync] Photo uploaded to Storage:", downloadURL);
      return downloadURL;
    }

    // Fallback: return the base64 data as-is if Storage isn't integrated yet
    console.warn("[Firestore Sync] Firebase Storage integration not loaded - using base64 fallback");
    return base64Data;
  }

  // Subscription plan limits — must match SUBSCRIPTION_PLANS in js/modules/subscriptions.js
  // Read limits from admin-configured subscription plans, falling back to defaults.
  // This ensures changes made in the admin panel are reflected immediately.
  const adminPlans = (typeof window !== 'undefined' && Array.isArray(window.subscriptionPlans))
    ? window.subscriptionPlans
    : [];
  const SUBSCRIPTION_MENU_LIMITS = {};
  adminPlans.forEach(plan => {
    if (plan && plan.id && typeof plan.maxItems === 'number') {
      SUBSCRIPTION_MENU_LIMITS[plan.id] = plan.maxItems;
    }
  });
  // Fallback defaults if admin plans haven't loaded yet
  if (!SUBSCRIPTION_MENU_LIMITS.free) SUBSCRIPTION_MENU_LIMITS.free = 10;
  if (!SUBSCRIPTION_MENU_LIMITS.basic) SUBSCRIPTION_MENU_LIMITS.basic = 50;
  if (!SUBSCRIPTION_MENU_LIMITS.premium) SUBSCRIPTION_MENU_LIMITS.premium = 200;

  async function saveMenuItem(menuItemData) {
    if (!currentUser || userRole !== "provider") {
      throw new Error("Must be logged in as provider");
    }

    // ── Pre-flight subscription limit check ──────────────────────────────────
    // Enforce before any write so the provider gets immediate, clear feedback.
    // The Cloud Function trigger (onMenuItemCreate) is the server-side backstop;
    // this check prevents the wasted write + deletion round-trip for normal use.
    const isNewItem = !menuItemData.id ||
      !window.__wtE_menuItems ||
      !window.__wtE_menuItems.some((m) => String(m.id) === String(menuItemData.id));

    if (isNewItem) {
      // Resolve the provider's plan from the live provider document.
      const rawPlan = String(
        (currentProviderDoc && currentProviderDoc.subscriptionPlan) || "free"
      ).toLowerCase();
      const effectivePlan = SUBSCRIPTION_MENU_LIMITS[rawPlan] !== undefined ? rawPlan : "free";
      const limit = SUBSCRIPTION_MENU_LIMITS[effectivePlan];

      // Use Firestore's count aggregate — zero reads of actual documents.
      const menuItemsRef = collection(firebaseDb, "menuItems");
      const countQuery = query(menuItemsRef, where("providerId", "==", currentUser.uid));
      const countSnap = await getCountFromServer(countQuery);
      const currentCount = countSnap.data().count;

      if (currentCount >= limit) {
        const err = new Error(
          `Menu item limit reached for your "${effectivePlan}" plan ` +
          `(${currentCount}/${limit} items). ` +
          `Please upgrade your subscription to add more items.`
        );
        err.code = "MENU_LIMIT_EXCEEDED";
        err.currentCount = currentCount;
        err.limit = limit;
        err.plan = effectivePlan;
        throw err;
      }
    }
    // ── End pre-flight check ─────────────────────────────────────────────────

    // If a base64 photo is provided, upload it to Firebase Storage
    // and replace it with the download URL in the document.
    let photoValue = menuItemData.photo || "";
    if (photoValue && photoValue.startsWith("data:")) {
      try {
        photoValue = await uploadPhotoToStorage(photoValue, currentUser.uid, menuItemData.id || Date.now());
      } catch (photoErr) {
        console.warn("[Firestore Sync] Photo upload skipped:", photoErr.message);
        // Keep the original base64 as fallback
        photoValue = menuItemData.photo;
      }
    }

    const menuItemDoc = {
      id: menuItemData.id || Date.now(),
      providerId: currentUser.uid,
      providerName: menuItemData.providerName,
      providerEmail: currentUser.email,
      providerPhone: menuItemData.providerPhone,
      providerAddress: menuItemData.providerAddress,
      providerType: menuItemData.providerType,
      foodName: menuItemData.foodName,
      foodDesc: menuItemData.foodDesc,
      ingredients: menuItemData.ingredients,
      drinks: menuItemData.drinks,
      photo: photoValue,
      // Store the original photo storage path for later deletion if needed
      photoStoragePath: photoValue && photoValue.startsWith("http") ? `menu-items/${currentUser.uid}/${menuItemData.id || Date.now()}` : "",
      price: menuItemData.price,
      servingsLeft: menuItemData.servingsLeft,

      // IMPORTANT: preserve/propagate "last servings update" timestamps.
      servingsLeftUpdatedAt: menuItemData.servingsLeftUpdatedAt || menuItemData.lastServingsUpdatedAt || serverTimestamp(),
      updatedAt: menuItemData.updatedAt || serverTimestamp(),

      is247: menuItemData.is247,
      providerTimezone: menuItemData.providerTimezone,
      availabilityStatus: menuItemData.availabilityStatus === "tomorrow" ? "tomorrow" : "today",
      availabilityDate: menuItemData.availabilityDate,
      openTime: menuItemData.openTime,
      closeTime: menuItemData.closeTime,
      afterHours: menuItemData.afterHours,
      offersDelivery: menuItemData.offersDelivery,
      offersPickup: menuItemData.offersPickup,
      dailySpecial: menuItemData.dailySpecial === true,
      portions: menuItemData.portions || [],
      mealComponents: Array.isArray(menuItemData.mealComponents) ? menuItemData.mealComponents : [],
      isActive: menuItemData.isActive != null ? menuItemData.isActive : true,
      country: menuItemData.country,
      mealType: menuItemData.mealType || '',

      // Preserve createdAt when editing existing items; otherwise use server timestamp.
      createdAt: menuItemData.createdAt || serverTimestamp()
    };

    await setDoc(doc(firebaseDb, "menuItems", String(menuItemDoc.id)), menuItemDoc);
    console.log("[Firestore Sync] Menu item saved:", menuItemDoc.id, "photoStoragePath:", menuItemDoc.photoStoragePath || "none");
    return menuItemDoc;
  }

  async function updateMenuItemActive(menuItemId, isActive) {
    await ensureFirebaseServicesReady();
    if (!firebaseDb || !currentUser || userRole !== "provider") {
      throw new Error("Must be logged in as a provider to change menu visibility.");
    }
    const id = String(menuItemId || "").trim();
    if (!id) throw new Error("Menu item ID is required.");

    await updateDoc(doc(firebaseDb, "menuItems", id), {
      isActive: Boolean(isActive),
      updatedAt: serverTimestamp(),
    });
  }

  async function updateOrderStatus(orderId, newStatus, note) {
    if (!currentUser || userRole !== "provider") {
      throw new Error("Must be logged in as provider");
    }

    const orderRef = doc(firebaseDb, "orders", String(orderId));
    const snap = await getDoc(orderRef);

    if (!snap.exists()) throw new Error("Order not found");

    const orderData = snap.data();
    if (orderData.providerId !== currentUser.uid) {
      throw new Error("Not authorized to update this order");
    }

    await updateDoc(orderRef, {
      status: newStatus,
      statusHistory: arrayUnion({
        status: newStatus,
        timestamp: new Date().toISOString(),
        note: note || "Status changed to " + newStatus
      })
    });
  }

  async function cancelOrderAsClient(orderId, note) {
    if (!currentUser || userRole !== "client") {
      throw new Error("Must be logged in as client");
    }

    const orderRef = doc(firebaseDb, "orders", String(orderId));
    const snap = await getDoc(orderRef);

    if (!snap.exists()) throw new Error("Order not found");

    const orderData = snap.data();

    // Client authorization: this order must belong to this client.
    if (orderData.clientId !== currentUser.uid) {
      throw new Error("Not authorized to cancel this order");
    }

    const currentStatus = orderData.status || "pending";

    // Match app policy: allow cancel only while pending/confirmed
    if (currentStatus !== "pending" && currentStatus !== "confirmed") {
      throw new Error("Cancellation is not available for this order status");
    }

    await updateDoc(orderRef, {
      status: "cancelled",
      statusHistory: arrayUnion({
        status: "cancelled",
        timestamp: new Date().toISOString(),
        note: note || "Order cancelled"
      })
    });
  }

  async function deleteOrderAsClient(orderId) {
    if (!currentUser || userRole !== "client") {
      throw new Error("Must be logged in as client");
    }

    const orderRef = doc(firebaseDb, "orders", String(orderId));
    const snap = await getDoc(orderRef);

    if (!snap.exists()) throw new Error("Order not found");

    const orderData = snap.data();

    // Client authorization: this order must belong to this client.
    if (orderData.clientId !== currentUser.uid) {
      throw new Error("Not authorized to delete this order");
    }

    await deleteDoc(orderRef);
  }

  function syncAllMenuItems(options = {}) {
    if (!firebaseDb) return;

    let selectedCountry = null;
    let pageSize = 24;
    let lastDoc = null;

    if (typeof options === "string") {
      selectedCountry = options || null;
    } else if (options && typeof options === "object") {
      selectedCountry = options.country || options.selectedCountry || window.selectedCountry || null;
      pageSize = Number(options.pageSize || options.limit || 24);
      lastDoc = options.lastDoc || options.startAfter || null;
    } else {
      selectedCountry = window.selectedCountry || null;
    }

    // Do not filter on isActive at query time: legacy menu items may omit the field.
    // Visibility is enforced in renderSearchResults/renderClientSearchResults (item.isActive !== false).
    const queryConstraints = [];

    if (selectedCountry) {
      queryConstraints.push(where("country", "==", selectedCountry));
    }
    queryConstraints.push(limit(pageSize));

    if (lastDoc) {
      queryConstraints.push(startAfter(lastDoc));
    }

    const q = query(collection(firebaseDb, "menuItems"), ...queryConstraints);
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const allMenuItems = [];
        snapshot.forEach((d) => allMenuItems.push(d.data()));
        if (typeof window.bridgeFirestoreMenuItemsToLocal === "function") {
          window.bridgeFirestoreMenuItemsToLocal(allMenuItems);
        } else {
          window.__wtE_allMenuItems = allMenuItems;
          window.menuItems = allMenuItems;
          if (typeof menuItems !== "undefined") {
            menuItems.length = 0;
            allMenuItems.forEach((item) => menuItems.push(item));
          }
        }
        console.log("[Firestore Sync] Synced", allMenuItems.length, "menu items for search (country:", selectedCountry, ")");

        // Try to use the bridge re-render function if available
        if (typeof window.triggerAppReRender === 'function') {
          window.triggerAppReRender();
        } else {
          // Fallback: directly call render functions
          if (typeof renderSearchResults === "function") renderSearchResults();
          if (typeof renderClientSearchResults === "function") renderClientSearchResults();
        }
      },
      (error) => console.log("[Firestore Sync] All menu items listener error:", error)
    );

    unsubscribeListeners.push(unsub);
  }

  async function init() {
    console.log("[Firestore Sync] Initializing (modular)...");

    // Initialize ErrorLogger if available
    if (typeof ErrorLogger !== 'undefined' && ErrorLogger.init) {
      try {
        await ErrorLogger.init();
        console.log("[Firestore Sync] ErrorLogger initialized");
      } catch (e) {
        console.warn("[Firestore Sync] ErrorLogger init failed:", e.message);
      }
    }

    const ok = await ensureFirebaseServicesReady();
    if (!ok) {
      console.log("[Firestore Sync] Firebase services not ready yet; will retry");
      if (typeof ErrorLogger !== 'undefined' && ErrorLogger.warning) {
        ErrorLogger.warning('firebase-integration.init', 'Firebase services not ready, will retry');
      }
      setTimeout(init, 200);
      return;
    }

    // Check if data migration from localStorage to Firestore is needed
    try {
      if (typeof window.isMigrationCompleted === 'function' && !window.isMigrationCompleted()) {
        console.log("[Firestore Sync] localStorage data migration may be needed");
        if (typeof window.checkMigrationStatus === 'function') {
          window.checkMigrationStatus().then(status => {
            if (status.needsMigration && status.needsMigration.length > 0) {
              console.log(`[Firestore Sync] ${status.needsMigration.length} collection(s) need migration:`, status.needsMigration.join(', '));
              if (typeof ErrorLogger !== 'undefined' && ErrorLogger.info) {
                ErrorLogger.info('firebase-integration.init', `Migration needed for ${status.needsMigration.length} collections`, { collections: status.needsMigration });
              }
            }
          }).catch(() => {});
        }
      }
    } catch (e) {
      // Migration check is non-critical
      console.log("[Firestore Sync] Migration check skipped:", e.message);
    }

    onAuthStateChanged(firebaseAuth, async (user) => {
      if (user) {
        console.log("[Firestore Sync] Auth state changed:", user.email, user.uid);
        // Clear stale role-scoped listeners before starting the next auth session.
        // This prevents the admin dashboard from continuing to listen while a client
        // or provider signs in on the same page, which caused admin counts to be
        // re-populated from the wrong user's permissions.
        if (typeof stopAdminRealTimeListeners === 'function') {
          stopAdminRealTimeListeners();
        }
        if (typeof stopAllListeners === 'function') {
          stopAllListeners();
        }
        if (typeof ErrorLogger !== 'undefined' && ErrorLogger.info) {
          ErrorLogger.info('firebase-integration.auth', `User authenticated: ${user.email}`, { uid: user.uid });
        }
        currentUser = user;

// ============ SECURITY FIX: Check for admin claims FIRST ============
        // If the user has admin:true custom claim, they are the super admin.
        // Skip getUserRole() and admin real-time listeners need to be handled
        // separately by the admin login flow in app.js.
        // This prevents non-admin users from triggering admin listener setup
        // during the brief window before performAdminLogin() checks claims.
        //
        // Admin authorization is 100% backend-driven: only the `admin: true`
        // (or `role: admin`) custom claim issued by Firebase Auth / Admin SDK /
        // Cloud Functions grants admin access. No client-side email whitelist
        // or hardcoded admin email is used.
        let isAdmin = false;

        // First attempt: try to get token with custom claims
        try {
          const idTokenResult = await user.getIdTokenResult(true);
          const customClaims = idTokenResult.claims || {};

          // Grant admin ONLY if the backend-issued custom claim is present.
          if (customClaims.admin === true || customClaims.role === "admin") {
            isAdmin = true;
            console.log("[Firestore Sync] Admin user detected via backend custom claim");
          }
        } catch (e) {
          // Token refresh failed - try with retries
          console.warn("[Firestore Sync] Token refresh failed, will retry with delay:", e.message);
          for (let retry = 1; retry <= 3; retry++) {
            try {
              await new Promise(r => setTimeout(r, 1000 * retry));
              const idTokenResult = await user.getIdTokenResult(true);
              const customClaims = idTokenResult.claims || {};

              // Claim-based admin check on retry
              if (customClaims.admin === true || customClaims.role === "admin") {
                isAdmin = true;
                console.log("[Firestore Sync] Admin user detected via backend custom claim after retry", retry);
              }
              break;
            } catch (retryErr) {
              console.warn(`[Firestore Sync] Token refresh retry ${retry}/3 failed:`, retryErr.message);
            }
          }
        }

        if (isAdmin) {
          console.log("[Firestore Sync] Admin user detected - skipping role sync");
          // For admin users, set role to admin and skip normal user sync
          userRole = "admin";
          syncAllMenuItems();
          return;
        }
        // ============ END ADMIN CLAIM CHECK ============

        try {
          const roleData = await getUserRole(user.uid);
          userRole = roleData.role;
          syncAllMenuItems();

          // If provider, keep subscriptionStatus live so suspend/activate updates without refresh.
          if (userRole === "provider") {
            // currentProviderDoc is set in getUserRole (best effort). Start live listener now.
            startLiveProviderSubscriptionSync();
          } else {
            stopLiveProviderSubscriptionSync();
          }

          // ============ TRIGGER RE-RENDER AFTER ROLE SYNC ============
          // After user role is determined and real-time listeners are set up,
          // trigger a re-render of all UI components so the app shows fresh data
          // from Firestore instead of stale localStorage data.
          if (typeof window.triggerAppReRender === 'function') {
            // Small delay to let the first snapshot data arrive
            setTimeout(() => {
              try {
                window.triggerAppReRender();
                console.log("[Firestore Sync] Re-render triggered after auth sync");
              } catch (renderErr) {
                console.warn("[Firestore Sync] Re-render after auth sync failed:", renderErr.message);
              }
            }, 500);
          }
          // ============ END RE-RENDER TRIGGER ============

        } catch (e) {
          console.warn("[Firestore Sync] getUserRole failed:", e);
          if (typeof ErrorLogger !== 'undefined' && ErrorLogger.error) {
            ErrorLogger.error('firebase-integration.getUserRole', 'Failed to get user role', e, { uid: user.uid });
          }
          stopLiveProviderSubscriptionSync();
        }
      } else {
        console.log("[Firestore Sync] User logged out");
        if (typeof ErrorLogger !== 'undefined' && ErrorLogger.info) {
          ErrorLogger.info('firebase-integration.auth', 'User signed out');
        }
        // ============ FIX: Stop admin real-time listeners on logout ============
        // When the admin user logs out, the onSnapshot listeners on clients and
        // providers collections continue running but the user is no longer
        // authenticated. This causes "Missing or insufficient permissions" errors.
        // We must explicitly stop these listeners here.
        stopAdminRealTimeListeners();
        if (typeof window.__stopFirestoreRealtimeSync === "function") {
          window.__stopFirestoreRealtimeSync();
        }
        // ============ END FIX ============
        stopAllListeners();
        stopLiveProviderSubscriptionSync();
        currentUser = null;
        userRole = null;
      }
    });

    firebaseReady = true;
    window.__firebaseIntegrationReady = true;

    console.log("[Firestore Sync] Integration loaded with data isolation (modular)");
  }

  // ============ GLOBAL FUNCTIONS ============
  window.firebaseSignUp = (email, password, role, userData) => signUp(email, password, role, userData);
  window.firebaseSignIn = (email, password) => signIn(email, password);

  /**
   * Role-detection entry point for an already-authenticated Firebase user.
   * Skips re-calling signInWithEmailAndPassword — uses the user object from
   * the caller's existing auth session. Called by auth-service.js after it
   * has already signed in to avoid the double sign-in bug.
   */
  window.firebaseSignInWithUser = async function firebaseSignInWithUser(user) {
    if (!user || !user.uid) {
      throw new Error("firebaseSignInWithUser: valid Firebase user object required.");
    }

    if (!(await ensureFirebaseServicesReady())) {
      throw new Error("Firebase not ready");
    }

    await rejectPendingProviderLogin(user);
    const trustedRole = await resolveServerRoleForCurrentUser(user);

    if (!trustedRole || trustedRole === "admin") {
      throw new Error(
        "This account is not authorized as a client or provider. Use the admin login flow for admin access."
      );
    }

    if (trustedRole === "provider") {
      if (typeof user.reload === "function") {
        await user.reload().catch(() => {});
      }
      if (user.emailVerified !== true) {
        await authSignOut(firebaseAuth).catch(() => {});
        throw new Error("Please verify your email before provider login.");
      }
    }

    console.log("[Firebase Auth] User role resolved (no re-sign-in):", user.uid, "role:", trustedRole);
    return getUserRole(user.uid);
  };

  window.firebaseSignOut = () => signOut();

  window.getCurrentUser = () => currentUser;
  window.getUserRole = () => userRole;
  window.isFirebaseReady = () => firebaseReady && currentUser !== null;

  // Admin/lookup helpers for modular Firestore reads
  window.firebaseGetClientByUid = (uid) => getClientByUid(uid);
  window.firebaseGetClientByEmail = (email) => getClientByEmail(email);
  window.firebaseUpsertClientFromAuth = (uid, email, nameGuess) => upsertClientFromAuth(uid, email, nameGuess);

  window.firebaseGetProviderByUid = (uid) => getProviderByUid(uid);
  window.firebaseGetProviderByEmail = (email) => getProviderByEmail(email);

  window.firebaseListClients = () => listClients();
  window.firebaseListProviders = () => listProviders();
  window.firebaseListReviews = () => listReviews();

  // ==================== ADMIN SUBSCRIPTION PERSISTENCE ====================
  // Required by app.js (Admin → Subscriptions panel).
  // app.js calls:
  //   window.firebaseAdminUpdateProviderSubscription({ providerId, subscriptionStatus, subscriptionPlan, ... })
  async function adminUpdateProviderSubscription(payload) {
    if (!(await ensureFirebaseServicesReady())) {
      throw new Error("Firebase not ready");
    }

    if (!payload || payload.providerId == null) {
      throw new Error("adminUpdateProviderSubscription: providerId missing");
    }

    const providerId = String(payload.providerId);

    console.log("[Admin] adminUpdateProviderSubscription payload", {
      providerId,
      subscriptionStatus: payload.subscriptionStatus,
      subscriptionPlan: payload.subscriptionPlan,
      subscriptionStart: payload.subscriptionStart,
      subscriptionEnd: payload.subscriptionEnd,
      approvedBy: payload.approvedBy ?? payload.subscriptionApprovedBy,
      approvedAt: payload.approvedAt ?? payload.subscriptionApprovedAt,
      rejectionReason: payload.rejectionReason ?? payload.subscriptionRejectionReason,
      suspendedReason: payload.suspendedReason ?? payload.subscriptionSuspendedReason,
      notes: payload.notes ?? payload.subscriptionNotes,
    });

    // Build update object first (so we can apply to multiple matching docs).
    //
    // Supports TWO payload shapes:
    // 1) Field-based:
    //    { subscriptionStatus, subscriptionPlan, subscriptionStart, subscriptionEnd, ... }
    // 2) Action-based (from admin delegated handler):
    //    { action, nextStatus, reason, plan, providerId, ... }
    const update = {};

    const action = payload.action;

    // Action → normalized nextStatus
    // (Fixes case where app.js sends `{ action: "activate" }` but does NOT include subscriptionStatus)
    let inferredStatus = (payload.subscriptionStatus != null ? payload.subscriptionStatus : null) ??
                          (payload.nextStatus != null ? payload.nextStatus : null) ??
                          null;

    if (inferredStatus == null && action) {
      const a = String(action).toLowerCase();
      if (a === "activate" || a === "approve" || a === "approved") inferredStatus = "approved";
      else if (a === "reject" || a === "rejected") inferredStatus = "rejected";
      else if (a === "suspend" || a === "suspended") inferredStatus = "suspended";
      else if (a === "renew" || a === "change-plan" || a === "changed-plan") {
        // Renew/change-plan should keep/restore approved if caller didn't specify.
        inferredStatus = payload.currentSubscriptionStatus || payload.subscriptionStatus || "approved";
      }
    }

    const inferredPlan =
      (payload.subscriptionPlan != null ? payload.subscriptionPlan : null) ??
      (payload.plan != null ? payload.plan : null) ??
      (payload.selectedPlan != null ? payload.selectedPlan : null) ??
      null;

    // Prefer explicit field-based keys; fall back to start/end derived from action where possible
    const inferredStart =
      (payload.subscriptionStart != null ? payload.subscriptionStart : null) ??
      (payload.startDate != null ? payload.startDate : null) ??
      (payload.subscriptionStartDate != null ? payload.subscriptionStartDate : null) ??
      null;

    let inferredEnd =
      (payload.subscriptionEnd != null ? payload.subscriptionEnd : null) ??
      (payload.endDate != null ? payload.endDate : null) ??
      (payload.subscriptionEndDate != null ? payload.subscriptionEndDate : null) ??
      null;

    if (inferredEnd == null && inferredStart != null) {
      // If caller passed a durationDays/planDurationDays, compute end.
      const durationDays =
        payload.subscriptionDurationDays ??
        payload.durationDays ??
        payload.planDurationDays ??
        payload.planDuration ??
        null;

      if (durationDays != null && !Number.isNaN(Number(durationDays))) {
        const startMs = new Date(inferredStart).getTime();
        if (!Number.isNaN(startMs)) {
          inferredEnd = new Date(startMs + Number(durationDays) * 24 * 60 * 60 * 1000).toISOString();
        }
      }
    }

    // Apply inferred normalized fields
    if (inferredStatus != null) update.subscriptionStatus = inferredStatus;
    if (inferredPlan != null) update.subscriptionPlan = inferredPlan;
    if (inferredStart != null) update.subscriptionStart = inferredStart;
    if (inferredEnd != null) update.subscriptionEnd = inferredEnd;

    // Admin fields
    const approvedBy =
      payload.approvedBy ?? payload.subscriptionApprovedBy ?? payload.approved_by ?? payload.approved_by_user ?? null;
    const approvedAt =
      payload.approvedAt ?? payload.subscriptionApprovedAt ?? payload.approved_at ?? null;

    if (approvedBy != null) update.subscriptionApprovedBy = approvedBy;
    if (approvedAt != null) update.subscriptionApprovedAt = approvedAt;

    // Reasons/notes
    const reason =
      payload.reason ??
      payload.rejectionReason ??
      payload.suspendedReason ??
      payload.notes ??
      payload.subscriptionNotes ??
      "";

    // Map reason + clear reasons based on status
    // Ensure Activate clears suspension/rejection reasons so provider login no longer shows suspended/rejected.
    if (update.subscriptionStatus === "rejected") {
      if (payload.subscriptionRejectionReason != null) update.subscriptionRejectionReason = payload.subscriptionRejectionReason;
      else if (payload.rejectionReason != null) update.subscriptionRejectionReason = payload.rejectionReason;
      else update.subscriptionRejectionReason = reason || "";

      // Clear suspended reason when rejecting
      update.subscriptionSuspendedReason = "";
    } else if (update.subscriptionStatus === "suspended") {
      if (payload.subscriptionSuspendedReason != null) update.subscriptionSuspendedReason = payload.subscriptionSuspendedReason;
      else if (payload.suspendedReason != null) update.subscriptionSuspendedReason = payload.suspendedReason;
      else update.subscriptionSuspendedReason = reason || "";

      // Clear rejection reason when suspending
      update.subscriptionRejectionReason = "";
    } else if (update.subscriptionStatus === "approved") {
      // Activation/approval/renew/change-plan should clear both reasons
      update.subscriptionSuspendedReason = "";
      update.subscriptionRejectionReason = "";
    } else {
      // If status isn't explicitly changed, keep legacy behavior: only set reasons when provided
      if (payload.subscriptionRejectionReason != null) update.subscriptionRejectionReason = payload.subscriptionRejectionReason;
      if (payload.subscriptionSuspendedReason != null) update.subscriptionSuspendedReason = payload.subscriptionSuspendedReason;
    }

    if (payload.subscriptionNotes != null) update.subscriptionNotes = payload.subscriptionNotes;
    if (payload.notes != null) update.subscriptionNotes = payload.notes;

    // HARD ISOLATION (FIX MIX-UPS):
    // Only update the single document whose ID exactly equals the providerId we received:
    //   providers/{providerId}
    //
    // Do NOT expand via other queries (uid/providerId) because those can match multiple docs
    // and cause cross-account subscription updates.
    const providerRef = doc(firebaseDb, "providers", providerId);
    const providerSnap = await getDoc(providerRef);

    if (!providerSnap || !providerSnap.exists()) {
      throw new Error(
        "Provider document not found for providerId=" +
          providerId +
          " (expected docId: providers/" +
          providerId +
          ")"
      );
    }

    console.log("[Admin] Applying provider subscription update (doc-id only)", {
      providerId,
      update,
      docIdToUpdate: providerRef.id,
    });

    await updateDoc(providerRef, update);

    // Verify write landed (read-back)
    const afterSnap = await getDoc(providerRef);
    const afterData = afterSnap && afterSnap.exists ? afterSnap.data() : null;

    console.log("[Admin][Verify] provider doc updated", {
      providerId,
      docId: providerRef.id,
      subscriptionStatus: afterData?.subscriptionStatus,
      subscriptionPlan: afterData?.subscriptionPlan,
      subscriptionEnd: afterData?.subscriptionEnd,
      subscriptionApprovedBy: afterData?.subscriptionApprovedBy,
      subscriptionApprovedAt: afterData?.subscriptionApprovedAt,
      subscriptionRejectionReason: afterData?.subscriptionRejectionReason,
      subscriptionSuspendedReason: afterData?.subscriptionSuspendedReason,
      subscriptionNotes: afterData?.subscriptionNotes,
    });

    console.log("[Admin] Updated provider subscription (1 doc):", {
      providerId,
      updatedCount: 1,
      update,
      updatedDocId: providerRef.id,
    });

    return { providerId, update };
  }

  window.firebaseAdminUpdateProviderSubscription = (payload) => adminUpdateProviderSubscription(payload);

  window.firebaseGetProviderSalesOrders = async ({ providerId, startAt, endAt } = {}) => {
    await ensureFirebaseServicesReady();
    if (!firebaseDb || !currentUser || !providerId) {
      throw new Error("Provider sales data is unavailable until the signed-in session is ready.");
    }

    const createdConstraints = [
      where("providerId", "==", String(providerId)),
      orderBy("createdAt", "desc"),
    ];
    if (startAt) createdConstraints.splice(1, 0, where("createdAt", ">=", startAt.toISOString()));
    if (endAt) createdConstraints.splice(1, 0, where("createdAt", "<", endAt.toISOString()));

    const paidConstraints = [
      where("providerId", "==", String(providerId)),
      orderBy("paidAt", "desc"),
    ];
    if (startAt) paidConstraints.splice(1, 0, where("paidAt", ">=", startAt.toISOString()));
    if (endAt) paidConstraints.splice(1, 0, where("paidAt", "<", endAt.toISOString()));

    const ordersCollection = collection(firebaseDb, "orders");
    const snapshots = await Promise.all([
      getDocs(query(ordersCollection, ...createdConstraints)),
      getDocs(query(ordersCollection, ...paidConstraints)),
    ]);
    const ordersById = new Map();
    snapshots.forEach((snapshot) => snapshot.docs.forEach((orderDoc) => {
      ordersById.set(orderDoc.id, { id: orderDoc.id, ...(orderDoc.data() || {}) });
    }));
    return Array.from(ordersById.values());
  };

  // End-of-day close reports (written only by the scheduled Cloud Function).
  window.firebaseGetProviderDailyCloses = async ({ providerId, startDate, endDate } = {}) => {
    await ensureFirebaseServicesReady();
    if (!firebaseDb || !currentUser || !providerId) {
      throw new Error("Daily close reports are unavailable until the signed-in session is ready.");
    }

    const constraints = [where("providerId", "==", String(providerId))];
    if (startDate) constraints.push(where("businessDate", ">=", String(startDate)));
    if (endDate) constraints.push(where("businessDate", "<=", String(endDate)));
    constraints.push(orderBy("businessDate", "desc"));

    const snapshot = await getDocs(query(collection(firebaseDb, "providerDailyCloses"), ...constraints));
    return snapshot.docs.map((closeDoc) => ({ id: closeDoc.id, ...(closeDoc.data() || {}) }));
  };

  // Walk-in (external POS) daily totals entered manually by the provider.
  window.firebaseGetProviderWalkInSales = async ({ providerId, startDate, endDate } = {}) => {
    await ensureFirebaseServicesReady();
    if (!firebaseDb || !currentUser || !providerId) {
      throw new Error("Walk-in sales are unavailable until the signed-in session is ready.");
    }

    const constraints = [where("providerId", "==", String(providerId))];
    if (startDate) constraints.push(where("businessDate", ">=", String(startDate)));
    if (endDate) constraints.push(where("businessDate", "<=", String(endDate)));
    constraints.push(orderBy("businessDate", "desc"));

    const snapshot = await getDocs(query(collection(firebaseDb, "providerWalkInSales"), ...constraints));
    return snapshot.docs.map((entryDoc) => ({ id: entryDoc.id, ...(entryDoc.data() || {}) }));
  };

  window.firebaseGetProviderReviews = async (providerId) => {
    await ensureFirebaseServicesReady();
    if (!firebaseDb || !providerId) {
      throw new Error("Provider reviews are unavailable until the signed-in session is ready.");
    }

    const snapshot = await getDocs(query(
      collection(firebaseDb, "reviews"),
      where("providerId", "==", String(providerId)),
      orderBy("createdAt", "desc")
    ));
    return snapshot.docs.map((reviewDoc) => ({ id: reviewDoc.id, ...(reviewDoc.data() || {}) }));
  };

  // Orders are server-authoritative. Do not expose legacy direct Firestore
  // mutation helpers to other browser modules.
  window.firebaseSaveOrder = () => Promise.reject(new Error("Use the createOrder Cloud Function."));
  window.firebaseSaveMenuItem = (menuItemData) => saveMenuItem(menuItemData);
  window.firebaseUpdateMenuItemActive = (menuItemId, isActive) => updateMenuItemActive(menuItemId, isActive);
  window.firebaseDeleteMenuItem = (menuItemId) => deleteMenuItem(menuItemId);
  window.firebaseUpdateOrderStatus = () => Promise.reject(new Error("Use the transitionOrder Cloud Function."));
  window.firebaseCancelOrder = () => Promise.reject(new Error("Use the transitionOrder Cloud Function."));
  window.firebaseDeleteOrderAsClient = (orderId) => deleteOrderAsClient(orderId);
  window.firebaseSaveReview = () => Promise.reject(new Error("Use the submitOrderReview Cloud Function."));

  // ==================== MEAL SUGGESTION FEEDBACK (Firebase-only) ====================

  /**
   * Save a single suggestion feedback document to Firestore.
   * Collection: suggestions_feedback
   * Doc ID: feedbackDoc.id (UUID generated client-side)
   */
  async function saveSuggestionFeedback(feedbackDoc) {
    await ensureFirebaseServicesReady();
    if (!firebaseDb) throw new Error("Firestore not ready");
    const ref = doc(firebaseDb, "suggestions_feedback", feedbackDoc.id);
    await setDoc(ref, feedbackDoc);
    return feedbackDoc;
  }

  /**
   * Load all past suggestion feedback for a given client (by clientId).
   * Used by the scoring engine to apply positive/negative provider weights.
   */
  async function loadSuggestionFeedback(clientId) {
    await ensureFirebaseServicesReady();
    if (!firebaseDb) return [];
    const q = query(
      collection(firebaseDb, "suggestions_feedback"),
      where("clientId", "==", clientId)
    );
    const snap = await getDocs(q);
    const results = [];
    snap.forEach(d => results.push({ id: d.id, ...d.data() }));
    return results;
  }

  window.firebaseSaveSuggestionFeedback = (feedbackDoc) => saveSuggestionFeedback(feedbackDoc);
  window.firebaseLoadSuggestionFeedback = (clientId) => loadSuggestionFeedback(clientId);

  // ==================== ADMIN BACKEND CALLS (Privileged) ====================
  // Privileged operations (delete, subscription approvals + notifications) must be backend-owned.
  const PROJECT_ID = window.firebaseConfig?.projectId || null;
  const FUNCTIONS_REGION = "us-central1";
  // Cloud Run hash for 2nd gen Cloud Functions (stable across redeployments)
  const CLOUD_RUN_HASH = "olj7d75erq";
  // Cloud Run region code (short form, e.g. "uc" for us-central1)
  const CLOUD_RUN_REGION = "uc";

  function getAdminFunctionsBaseUrl() {
    if (!PROJECT_ID) throw new Error("Missing firebaseConfig.projectId");
    // 2nd gen Cloud Functions use Cloud Run URLs. The cloudfunctions.net proxy
    // does not properly forward CORS preflight (OPTIONS) requests, so we use
    // the run.app URL directly.
    // Pattern: https://{functionName}-{hash}-{regionCode}.a.run.app
    return `https://${FUNCTIONS_REGION}-${PROJECT_ID}.cloudfunctions.net`;
  }

  async function adminHttpPost(functionName, body) {
    const user = window.getCurrentUser ? window.getCurrentUser() : null;
    const admin = window.currentAdmin || null;

    // Build both URL patterns to try
    // 1. cloudfunctions.net (1st gen - more reliable for CORS)
    // 2. run.app (2nd gen - may be deployed)
    const PROJECT_ID = window.firebaseConfig?.projectId || "what-to-eat-e5ce9";
    const FUNCTIONS_REGION = "us-central1";
    const url1 = `https://${FUNCTIONS_REGION}-${PROJECT_ID}.cloudfunctions.net/${functionName}`;
    const url2 = `https://${functionName}-${CLOUD_RUN_HASH}-${CLOUD_RUN_REGION}.a.run.app`;

    // Try URLs in order: cloudfunctions.net first (more reliable), then run.app
    const urlsToTry = [url1, url2];

    // If admin is logged in via Firestore (not Firebase Auth), pass admin credentials directly
    if (admin && admin.username && admin.authenticatedVia === 'firestore') {
      const payload = body || {};
      payload._adminUsername = admin.username;
      payload._adminPassword = admin._password || '';

      let lastError = null;
      const appCheckToken = await getRequiredAppCheckToken();
      for (const url of urlsToTry) {
        try {
          const res = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Firebase-AppCheck": appCheckToken
            },
            body: JSON.stringify(payload)
          });

          const text = await res.text();
          let data = null;
          try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }

          if (res.ok) {
            console.log(`[adminHttpPost] Success with URL: ${url}`);
            return data;
          }
          lastError = data?.error || `Request failed (${res.status})`;
        } catch (e) {
          lastError = e.message;
          console.warn(`[adminHttpPost] URL ${url} failed:`, e.message);
        }
      }
      throw new Error(lastError || "All URLs failed");
    }

    // Standard Firebase Auth token approach
    if (!user || typeof user.getIdToken !== "function") throw new Error("Not authenticated (no currentUser)");

    // CRITICAL: Force refresh the token to pick up the latest custom claims (admin: true)
    const token = await user.getIdToken(true);
    const appCheckToken = await getRequiredAppCheckToken();
    console.log(`[adminHttpPost] Calling ${functionName} with token (force-refreshed)`);

    let lastError = null;
    for (const url of urlsToTry) {
      try {
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Firebase-AppCheck": appCheckToken,
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body || {})
        });

        const text = await res.text();
        let data = null;
        try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }

        if (res.ok) {
          console.log(`[adminHttpPost] Success with URL: ${url}`);
          return data;
        }
        lastError = data?.error || `Request failed (${res.status})`;
        console.warn(`[adminHttpPost] URL ${url} returned ${res.status}:`, lastError);
      } catch (e) {
        lastError = e.message;
        console.warn(`[adminHttpPost] URL ${url} failed:`, e.message);
      }
    }

    throw new Error(lastError || "All URLs failed");
  }

  window.adminBackendDeleteClient = (payload) => adminHttpPost("adminDeleteClient", payload);
  window.adminBackendDeleteProvider = (payload) => adminHttpPost("adminDeleteProvider", payload);
  window.adminBackendDeleteAuthUser = (payload) => adminHttpPost("adminDeleteAuthUser", payload);
  window.adminBackendDeleteAllTestUsers = () => adminHttpPost("adminDeleteAllTestUsers", {});
  window.adminBackendUpdateProviderSubscription = (payload) =>
    adminHttpPost("adminUpdateProviderSubscription", payload);

  // ==================== ADMIN DELETE HELPERS (Super Admin) ====================
  // Robust delete-by-uid-or-id runtime contract for admin UI buttons.

  async function deleteClientByUidOrId(uidOrId) {
    if (!firebaseReady || !firebaseDb) throw new Error("Firebase not ready");
    if (uidOrId == null || String(uidOrId).trim() === "") throw new Error("uidOrId missing");

    const key = String(uidOrId);

    // Try direct doc id first (clients/{key})
    const directRef = doc(firebaseDb, "clients", key);
    const directSnap = await getDoc(directRef);
    if (directSnap.exists()) {
      await deleteDoc(directRef);
      return { deleted: true, usedDocId: key };
    }

    // Fallback: search by uid field or id field
    const uidQ = query(collection(firebaseDb, "clients"), where("uid", "==", key));
    const uidSnap = await getDocs(uidQ);
    if (!uidSnap.empty) {
      let targetDoc = null;
      uidSnap.forEach(d => { if (!targetDoc) targetDoc = d; });
      if (targetDoc) {
        await deleteDoc(doc(firebaseDb, "clients", String(targetDoc.id)));
        return { deleted: true, usedDocId: String(targetDoc.id) };
      }
    }

    const idQ = query(collection(firebaseDb, "clients"), where("id", "==", key));
    const idSnap = await getDocs(idQ);
    if (!idSnap.empty) {
      let targetDoc = null;
      idSnap.forEach(d => { if (!targetDoc) targetDoc = d; });
      if (targetDoc) {
        await deleteDoc(doc(firebaseDb, "clients", String(targetDoc.id)));
        return { deleted: true, usedDocId: String(targetDoc.id) };
      }
    }

    return { deleted: false, usedDocId: null };
  }

  async function deleteProviderByUidOrId(uidOrId) {
    if (!firebaseReady || !firebaseDb) throw new Error("Firebase not ready");
    if (uidOrId == null || String(uidOrId).trim() === "") throw new Error("uidOrId missing");

    const key = String(uidOrId);

    // Try direct doc id first (providers/{key})
    const directRef = doc(firebaseDb, "providers", key);
    const directSnap = await getDoc(directRef);
    if (directSnap.exists()) {
      await deleteDoc(directRef);
      return { deleted: true, usedDocId: key };
    }

    // Fallback: search by uid field or id/providerId field
    const uidQ = query(collection(firebaseDb, "providers"), where("uid", "==", key));
    const uidSnap = await getDocs(uidQ);
    if (!uidSnap.empty) {
      let targetDoc = null;
      uidSnap.forEach(d => { if (!targetDoc) targetDoc = d; });
      if (targetDoc) {
        await deleteDoc(doc(firebaseDb, "providers", String(targetDoc.id)));
        return { deleted: true, usedDocId: String(targetDoc.id) };
      }
    }

    const idQ = query(collection(firebaseDb, "providers"), where("id", "==", key));
    const idSnap = await getDocs(idQ);
    if (!idSnap.empty) {
      let targetDoc = null;
      idSnap.forEach(d => { if (!targetDoc) targetDoc = d; });
      if (targetDoc) {
        await deleteDoc(doc(firebaseDb, "providers", String(targetDoc.id)));
        return { deleted: true, usedDocId: String(targetDoc.id) };
      }
    }

    const providerIdQ = query(collection(firebaseDb, "providers"), where("providerId", "==", key));
    const pidSnap = await getDocs(providerIdQ);
    if (!pidSnap.empty) {
      let targetDoc = null;
      pidSnap.forEach(d => { if (!targetDoc) targetDoc = d; });
      if (targetDoc) {
        await deleteDoc(doc(firebaseDb, "providers", String(targetDoc.id)));
        return { deleted: true, usedDocId: String(targetDoc.id) };
      }
    }

    return { deleted: false, usedDocId: null };
  }

  // PRIVILEGED ADMIN DELETE - MUST BE BACKEND ONLY.
  // Client must not perform privileged deletes directly (security requirement).
  // window.firebaseDeleteClientByUidOrId = (uidOrId) => deleteClientByUidOrId(uidOrId);
  // window.firebaseDeleteProviderByUidOrId = (uidOrId) => deleteProviderByUidOrId(uidOrId);

  async function deleteClientByEmail(emailOrPhoneOrName) {
    if (!firebaseReady || !firebaseDb) throw new Error("Firebase not ready");
    if (emailOrPhoneOrName == null || String(emailOrPhoneOrName).trim() === "") throw new Error("emailOrPhoneOrName missing");
    const key = String(emailOrPhoneOrName).trim();
    const keyLower = key.toLowerCase();

    // Try direct doc id first (clients/{key})
    const directRef = doc(firebaseDb, "clients", key);
    const directSnap = await getDoc(directRef);
    if (directSnap.exists()) {
      await deleteDoc(directRef);
      return { deleted: true, usedField: "docId", usedDocId: key };
    }

    // Fallback search by common client fields: uid, email, phone, name, id
    // Also try lowercase for email fields (schema inconsistencies across versions).
    const candidates = ["uid", "email", "phone", "name", "id"];
    for (const field of candidates) {
      try {
        const qVal = (field === "email" ? keyLower : key);
        const q = query(collection(firebaseDb, "clients"), where(field, "==", qVal));
        const snap = await getDocs(q);
        if (!snap.empty) {
          let targetDoc = null;
          snap.forEach((d) => { if (!targetDoc) targetDoc = d; });
          if (targetDoc) {
            await deleteDoc(doc(firebaseDb, "clients", String(targetDoc.id)));
            return { deleted: true, usedField: field, usedDocId: String(targetDoc.id) };
          }
        }
      } catch (e) {
        // ignore field query errors and keep trying others
      }
    }

    return { deleted: false, usedField: null, usedDocId: null };
  }

  async function deleteProviderByEmail(emailOrPhoneOrName) {
    if (!firebaseReady || !firebaseDb) throw new Error("Firebase not ready");
    if (emailOrPhoneOrName == null || String(emailOrPhoneOrName).trim() === "") throw new Error("emailOrPhoneOrName missing");
    const key = String(emailOrPhoneOrName).trim();
    const keyLower = key.toLowerCase();

    // Try direct doc id first (providers/{key})
    const directRef = doc(firebaseDb, "providers", key);
    const directSnap = await getDoc(directRef);
    if (directSnap.exists()) {
      await deleteDoc(directRef);
      return { deleted: true, usedField: "docId", usedDocId: key };
    }

    // Fallback search by common provider fields (schema varies across versions)
    // Also try lowercase for email fields (schema inconsistencies across versions).
    const candidates = [
      "uid",
      "email",
      "providerEmail",
      "ownerEmail",
      "phone",
      "name",
      "id",
      "providerId"
    ];
    for (const field of candidates) {
      try {
        const qVal =
          (field === "email" || field === "providerEmail" || field === "ownerEmail")
            ? keyLower
            : key;
        const q = query(collection(firebaseDb, "providers"), where(field, "==", qVal));
        const snap = await getDocs(q);
        if (!snap.empty) {
          let targetDoc = null;
          snap.forEach((d) => { if (!targetDoc) targetDoc = d; });
          if (targetDoc) {
            await deleteDoc(doc(firebaseDb, "providers", String(targetDoc.id)));
            return { deleted: true, usedField: field, usedDocId: String(targetDoc.id) };
          }
        }
      } catch (e) {
        // ignore field query errors and keep trying others
      }
    }

    return { deleted: false, usedField: null, usedDocId: null };
  }

  // PRIVILEGED ADMIN DELETE - MUST BE BACKEND ONLY.
  // window.firebaseDeleteClientByEmailOrUidOrId = (emailOrPhoneOrName) => deleteClientByEmail(emailOrPhoneOrName);
  // window.firebaseDeleteProviderByEmailOrUidOrId = (emailOrPhoneOrName) => deleteProviderByEmail(emailOrPhoneOrName);


  window.syncAllMenuItems = () => syncAllMenuItems();

  // kick off
  init();
})();
